import axios from 'axios'

const baseURL = import.meta.env.VITE_API_BASE || '/api'
const ACCESS_TOKEN_KEY = 'alphalens:access_token'
const REFRESH_TOKEN_KEY = 'alphalens:refresh_token'
const USER_KEY = 'alphalens:user'

export const api = axios.create({
  baseURL,
  timeout: 30_000
})

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    // access token 过期: 先尝试用 refresh token 换新并重放原请求 (只重试一次),
    // 避免表单/聊天草稿因整页跳转登录而丢失; 刷新失败才真正登出
    if (
      error.response?.status === 401 &&
      !original?._retried &&
      getRefreshToken() &&
      !original?.url?.includes('/auth/refresh') &&
      !original?.url?.includes('/auth/login')
    ) {
      original._retried = true
      try {
        const refreshed = await refreshTokens()
        if (refreshed) {
          original.headers = original.headers ?? {}
          original.headers.Authorization = `Bearer ${refreshed}`
          return api(original)
        }
      } catch {
        // 刷新失败, 走下方登出逻辑
      }
    }
    if (error.response?.status === 401) {
      clearAuth()
      if (window.location.pathname !== '/login') {
        const currentPath = window.location.pathname + window.location.search
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
      }
    }
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail
      if (typeof detail === 'string') {
        error.message = detail
      } else if (Array.isArray(detail)) {
        error.message = detail
          .map((d: any) => {
            const loc = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : 'param'
            return loc ? `${loc}: ${d.msg}` : d.msg
          })
          .join('; ')
      }
    }
    throw error
  }
)

// 并发 401 时只发一次 refresh, 其余请求等待同一次刷新完成
let refreshPromise: Promise<string | null> | null = null

async function refreshTokens(): Promise<string | null> {
  refreshPromise ??= (async () => {
    const rt = getRefreshToken()
    if (!rt) return null
    try {
      // 用独立 axios 调用, 避免命中本拦截器造成递归
      const r = await axios.post<AuthTokens>(
        `${baseURL}/auth/refresh`,
        { refresh_token: rt },
        { timeout: 10_000 }
      )
      persistAuth(r.data)
      return r.data.access_token
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}


export interface AuthUser {
  user_id: string
  username: string
  email?: string | null
  role: string
  status?: string
}

export interface LLMSettings {
  active_config_id: string
  updated_at?: string
  configs: Array<{
    config_id: string
    name: string
    provider: string
    model: string
    quick_model: string
    base_url: string
    temperature: number
    updated_at?: string
    has_api_key: boolean
  }>
}

export interface AuthTokens {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in?: number
  user?: AuthUser
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY)
  return raw ? JSON.parse(raw) as AuthUser : null
}

export function isAuthenticated(): boolean {
  return !!getAccessToken()
}

export function persistAuth(tokens: AuthTokens) {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token)
  if (tokens.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
  }
  if (tokens.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(tokens.user))
  }
}

export function clearAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

/* ==========================================================================
 * 一次性流票据 (ALENS-128)
 *
 * EventSource 与 WebSocket 握手都带不了 Authorization 头, 历史实现把长期有效
 * 的 JWT 塞进 URL query —— query 会进浏览器历史、反向代理与后端访问日志,
 * 等于把"可复用的登录凭证"写进日志系统, 能读日志即可冒用会话。
 *
 * 改为一次性票据:
 *   - 建连前用 Authorization 头换一张 ticket (后端存 Redis, TTL 60s, 取出即删);
 *   - 连接 URL 只带 ticket, 不带 JWT;
 *   - 票据是一次性的 -> 每次建连 (含断线重连) 都必须换新票据。
 *
 * 因此对外返回的是**句柄**而不是 EventSource: EventSource 要等票据到手才能
 * 创建, 而调用方要能同步拿到句柄并 close() 它。句柄内部负责换票、建连与退避
 * 重连。
 * ========================================================================== */

/** 流连接句柄: 调用方只需要 close() 与 (可选) 覆写 onerror。 */
export interface StreamHandle {
  close: () => void
  onerror: ((e: Event) => void) | null
}

/** 换一张一次性流票据 (后端 POST /api/auth/stream-ticket)。 */
export async function fetchStreamTicket(): Promise<string> {
  const r = await api.post('/auth/stream-ticket')
  const ticket = r.data?.ticket
  if (!ticket) throw new Error('stream ticket 未返回')
  return String(ticket)
}

//: 断线重连退避 (ms)。第一档接近浏览器原生 EventSource 重连间隔 (~3s)。
const STREAM_RETRY_DELAYS_MS = [1000, 2000, 3000, 5000, 8000, 15000, 30000]

/**
 * 建立一条 SSE 连接 (票据方案)。
 *
 * @param buildPath 由票据拼出完整 URL
 * @param attach    把事件监听挂到新建的 EventSource 上
 * @param onDrop    连接断开时的回调 (调用方据此回查权威终态)
 * @param autoReconnect 断线后是否自动换票重连。带 onDone 的流由调用方回查
 *                      终态接管, 不重连; 进度类流沿用浏览器原生重连语义。
 */
function openTicketStream(
  buildPath: (ticket: string) => string,
  attach: (es: EventSource) => void,
  opts: { onDrop?: () => void; autoReconnect?: boolean } = {},
): StreamHandle {
  let es: EventSource | null = null
  let closed = false
  let retries = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  const handle: StreamHandle = {
    onerror: (e) => console.error('SSE error', e),
    close() {
      closed = true
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      es?.close()
      es = null
    },
  }

  function scheduleReconnect() {
    if (closed || timer) return
    const delay = STREAM_RETRY_DELAYS_MS[Math.min(retries, STREAM_RETRY_DELAYS_MS.length - 1)]
    retries += 1
    timer = setTimeout(() => {
      timer = null
      void connect()
    }, delay)
  }

  async function connect() {
    if (closed) return
    let ticket: string
    try {
      ticket = await fetchStreamTicket()
    } catch (e) {
      console.error('SSE: 获取流票据失败', e)
      handle.onerror?.(new Event('error'))
      scheduleReconnect()
      return
    }
    if (closed) return
    es = new EventSource(buildPath(ticket))
    attach(es)
    es.onerror = (e) => {
      // 调用方先处理 (它可能在自己的回调里 close())
      try {
        handle.onerror?.(e)
      } catch (err) {
        console.error('SSE onerror 回调异常', err)
      }
      if (closed) return
      es?.close()
      es = null
      opts.onDrop?.()
      if (opts.autoReconnect) scheduleReconnect()
    }
    retries = 0
  }

  void connect()
  return handle
}

export async function register(body: {
  username: string
  password: string
  email?: string
  invite_code?: string
}): Promise<{ registered: boolean; user: AuthUser }> {
  const r = await api.post('/auth/register', body)
  return r.data
}

export async function login(body: {
  username: string
  password: string
}): Promise<AuthTokens> {
  const r = await api.post<AuthTokens>('/auth/login', body)
  persistAuth(r.data)
  return r.data
}

export async function getMe(): Promise<AuthUser> {
  const r = await api.get<AuthUser>('/auth/me')
  localStorage.setItem(USER_KEY, JSON.stringify(r.data))
  return r.data
}

export function logout() {
  clearAuth()
}

export async function changePassword(body: {
  current_password: string
  new_password: string
}): Promise<{ changed: boolean }> {
  const r = await api.post('/auth/change-password', body)
  return r.data
}

export async function getLLMSettings(): Promise<LLMSettings> {
  const r = await api.get<LLMSettings>('/system/llm')
  return r.data
}

export async function updateLLMSettings(body: {
  active_config_id: string
  configs: Array<{
    config_id?: string
    name: string
    provider: string
    model: string
    quick_model: string
    base_url: string
    api_key?: string
    temperature: number
  }>
}): Promise<{ saved: boolean; llm: LLMSettings }> {
  const r = await api.put('/system/llm', body)
  return r.data
}

export async function testLLMConfig(body: {
  config_id?: string
  name?: string
  provider: string
  model: string
  quick_model?: string
  base_url?: string
  api_key?: string
  temperature?: number
}): Promise<{ ok: boolean; latency_ms?: number; reply?: string; error?: string; message: string }> {
  const r = await api.post('/system/llm/test', body)
  return r.data
}

export async function resetLLMSettings(): Promise<{ reset: boolean; message: string; llm: LLMSettings }> {
  const r = await api.post('/system/llm/reset')
  return r.data
}

// ==================== Types ====================

export type AnalysisDepth = 'quick' | 'standard' | 'deep'

export interface AnalyzeRequest {
  query: string
  depth?: AnalysisDepth
  /** point-in-time 分析基准日 (YYYY-MM-DD, 今天或过去的日历日); 缺省实时模式。
   *  非法/未来日期后端 422 拒绝, 不静默降级实时 (ALENS-191)。 */
  trade_date?: string
}

export interface AnalyzeResponse {
  analysis_id: string
  status: string
  created_at: string
}

export interface ProgressEvent {
  node: string
  status: 'started' | 'completed' | 'failed'
  detail: string
  timestamp: string
}

export interface SniperLevels {
  entry: number | null
  stop_loss: number | null
  target_1: number | null
  target_2: number | null
  risk_reward_ratio: number | null
  notes: string | null
}

export interface ChecklistItem {
  name: string
  status: 'satisfied' | 'caution' | 'violated'
  value: string | null
  detail: string | null
}

export interface PhaseDecision {
  phase: string
  phase_context: string
  action_window: string
  immediate_action: string
  watch_conditions: string[]
  next_check_time: string | null
  confidence_reason: string
  data_limitations: string[]
}

export interface DecisionDashboard {
  stock_name: string
  symbol: string
  market: string
  decision_type: 'buy' | 'hold' | 'sell'
  confidence_level: 'low' | 'medium' | 'high'
  confidence: number
  sentiment_score: number
  core_conclusion: string
  operation_advice: string
  sniper_levels: SniperLevels
  phase_decision: PhaseDecision
  key_points: string[]
  risk_warning: string[]
  checklist: ChecklistItem[]
  analysis_summary: string
  trend_prediction: string
  data_quality: string
  triggered_rules: string[]
}

// ==================== Analysis Report 结构化面板类型 (ALENS-194) ====================
// analysis 文档 (decisions 集合) 顶层字段, ReportView 各面板直接消费

/** ResearchManager 反中庸裁决 (多空辩论结果) */
export interface DebateResult {
  rating: 'Buy' | 'Overweight' | 'Hold' | 'Underweight' | 'Sell'
  deciding_side: 'bull' | 'bear' | 'balanced'
  rationale: string
  consensus?: string
  strategic_actions: string[]
  bull_view: { summary: string; evidence: string[]; score: number }
  bear_view: { summary: string; evidence: string[]; score: number }
  disagreement_points: string[]
  overall_score: number
  rounds_used?: number
  _failed?: boolean
}

/** TraderAgent 交易提案 (绝对价格执行矩阵) */
export interface TradeProposal {
  action: 'buy' | 'hold' | 'sell'
  reasoning: string
  entry_low: number | null
  entry_high: number | null
  stop_loss: number | null
  target_1: number | null
  target_2: number | null
  risk_reward_ratio: number | null
  position_sizing: string
  holding_period: string
  _failed?: boolean
}

/** 三方风控质询单条 */
export interface RiskReview {
  stance: 'aggressive' | 'conservative' | 'neutral'
  argument: string
  concerns: string[]
  position_advice: string
  _failed?: boolean
}

/** 芒格逆向排雷报告 (纯代码 Decimal 验算) */
export interface MungerInversionReport {
  symbol?: string
  generated_at?: string
  veto_triggered: boolean
  fatal_flaws: string[]
  warning_signals: string[]
  safety_verdict: 'safe' | 'caution' | 'deadly'
  metrics_snapshot: {
    altman_z?: number | null
    beneish_m?: number | null
    goodwill_ratio?: number | null
    pledge_ratio?: number | null
    cash_to_short_debt?: number | null
    interest_coverage?: number | null
    gross_margin?: number | null
    roe?: number | null
    [key: string]: any
  }
}

/** ALENS-180 双向反思条目 (成功/失败均反思, lesson 为散文) */
export interface ReflectionItem {
  analysis_id: string
  symbol: string
  outcome: 'success' | 'failure'
  lesson: string
  lessons_learned: string[]
  failure_reason: string
  raw_return?: number | null
  alpha_return?: number | null
  holding_days?: number | null
  resolution_date?: string | null
  rating?: string | null
  created_at?: string
  version?: number
}

export interface IndexQuote {
  name: string
  symbol: string
  last: number | null
  change_pct: number | string | null
  error?: string
}

export interface MarketReview {
  market: string
  report_md: string
  indices: IndexQuote[]
  north_flow: any
  regime: any
  sectors: any[]
  macro: Record<string, any>
  created_at: string
  cached?: boolean
  age_seconds?: number
}

export interface PortfolioPosition {
  _id: string
  user: string
  symbol: string
  market: string
  stock_name?: string
  quantity: number
  cost_price: number
  cost_currency: string
  purchase_date: string
  notes?: string
  current_price?: number | null
  cost_value?: number
  current_value?: number | null
  pnl?: number | null
  pnl_pct?: number | null
  created_at: string
  updated_at: string
}

export interface PortfolioSummary {
  user: string
  total_positions: number
  total_cost: number
  total_value: number
  total_pnl: number
  total_pnl_pct: number
  positions: PortfolioPosition[]
  updated_at: string
}

export interface AlertRule {
  _id: string
  user: string
  symbol: string
  market: string
  rule_type: string
  threshold: number | null
  active: boolean
  cooldown_seconds: number
  last_triggered_at: string | null
  channels: string[]
  notes?: string
  created_at: string
}

export interface AlertHistory {
  _id: string
  user: string
  symbol: string
  market: string
  rule_type: string
  threshold: number | null
  value: any
  message: string
  triggered_at: string
}

// ==================== Analyze ====================

export async function createAnalysis(body: AnalyzeRequest): Promise<AnalyzeResponse> {
  const r = await api.post<AnalyzeResponse>('/analyze', body)
  return r.data
}

export async function getAnalysis(id: string): Promise<any> {
  const r = await api.get(`/analyze/${id}`)
  return r.data
}

export async function listAnalyses(params: { limit?: number; symbol?: string; status?: string } = {}): Promise<any> {
  const r = await api.get('/analyze', { params })
  return r.data
}

export function openProgressStream(
  analysisId: string,
  onEvent: (e: ProgressEvent) => void,
  onReplayDone?: () => void,
): StreamHandle {
  return openTicketStream(
    (ticket) => `${baseURL}/analyze/${analysisId}/stream?ticket=${encodeURIComponent(ticket)}`,
    (es) => {
      es.addEventListener('progress', (ev) => {
        try {
          onEvent(JSON.parse(ev.data))
        } catch (e) {
          console.error('SSE parse error', e)
        }
      })
      // replay_done: history replay finished, everything after it is live
      if (onReplayDone) {
        es.addEventListener('replay_done', () => onReplayDone())
      }
    },
    { autoReconnect: true },
  )
}

// ==================== Auto Research (自动投研管线) ====================

export interface ResearchCreateResponse {
  research_id: string
  status: string
  created_at: string
}

export async function createResearch(body: { symbol: string; name?: string }): Promise<ResearchCreateResponse> {
  const r = await api.post<ResearchCreateResponse>('/research', body)
  return r.data
}

export async function getResearch(id: string): Promise<any> {
  const r = await api.get(`/research/${id}`)
  return r.data
}

export async function listResearch(params: { limit?: number; symbol?: string; status?: string } = {}): Promise<any> {
  const r = await api.get('/research', { params })
  return r.data
}

/** 下载投研报告 PDF (blob; GET 需带 Authorization, 不能用 <a href>) */
export async function downloadResearchPdf(researchId: string, symbol: string): Promise<void> {
  const r = await api.get(`/research/${researchId}/pdf`, {
    responseType: 'blob',
    timeout: 120_000,
  })
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `alphalens_research_${symbol}_${researchId.slice(0, 8)}.pdf`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** 订阅自动投研管线进度 (SSE, 与 openProgressStream 同构) */
export function openResearchStream(
  researchId: string,
  onEvent: (e: ProgressEvent) => void,
  onReplayDone?: () => void,
): StreamHandle {
  return openTicketStream(
    (ticket) => `${baseURL}/research/${researchId}/stream?ticket=${encodeURIComponent(ticket)}`,
    (es) => {
      es.addEventListener('progress', (ev) => {
        try {
          onEvent(JSON.parse(ev.data))
        } catch (e) {
          console.error('SSE parse error', e)
        }
      })
      // replay_done: history replay finished, everything after it is live
      if (onReplayDone) {
        es.addEventListener('replay_done', () => onReplayDone())
      }
    },
    { autoReconnect: true },
  )
}

// ==================== Investment Thesis & Checklist (ai-berkshire 有机融合) ====================

export type ThesisStatus = 'intact' | 'warning' | 'drifted' | 'broken' | 'archived'

export type RedLineOperator = '<' | '<=' | '>' | '>='

/** 退出红线: 结构化指标红线 (metric_key+operator+threshold) 或纯文本舆情关键词红线 (仅 condition) */
export interface FalsifiableCondition {
  condition: string
  metric_key?: string
  operator?: RedLineOperator
  threshold?: number
}

export interface InvestmentThesis {
  thesis_id: string
  user_id: string
  symbol: string
  name: string
  status: ThesisStatus
  created_at: string
  updated_at: string
  core_thesis: string[]
  catalysts: Array<{ event: string; status?: string }>
  falsifiable_conditions: FalsifiableCondition[]
  target_price_range?: { min?: number; max?: number }
  source_research_id?: string
  initial_metrics?: Record<string, any>
  drift_history?: any[]
}

export interface BuffettChecklistResult {
  symbol: string
  name: string
  score: number
  passed_gates_count: number
  total_gates: number
  verdict: 'pass' | 'grey_zone' | 'reject'
  summary: string
  red_flags: string[]
  gates: Record<string, {
    gate_name: string
    gate_key: string
    weight: number
    score: number
    passed: boolean
    checks: Array<{ name: string; passed: boolean; detail: string }>
  }>
}

export async function createInvestmentThesis(body: Partial<InvestmentThesis>): Promise<InvestmentThesis> {
  const r = await api.post<InvestmentThesis>('/theses', body)
  return r.data
}

/** 从已完成分析一键生成投资论文档案 (ALENS-194) */
export async function createThesisFromAnalysis(
  analysisId: string,
  opts: { thesis_title?: string; custom_redlines?: FalsifiableCondition[] } = {},
): Promise<InvestmentThesis> {
  const r = await api.post<InvestmentThesis>('/theses/create-from-analysis', {
    analysis_id: analysisId,
    ...opts,
  })
  return r.data
}

export async function listInvestmentTheses(params: { symbol?: string; status?: string; limit?: number } = {}): Promise<{ count: number; items: InvestmentThesis[] }> {
  const r = await api.get('/theses', { params })
  return r.data
}

export async function getInvestmentThesis(thesisId: string): Promise<InvestmentThesis> {
  const r = await api.get(`/theses/${thesisId}`)
  return r.data
}

export async function getThesisBySymbol(symbol: string): Promise<InvestmentThesis | null> {
  try {
    const r = await api.get(`/theses/by-symbol/${symbol}`)
    return r.data
  } catch (err: any) {
    if (err.response?.status === 404) return null
    throw err
  }
}

export async function updateInvestmentThesis(thesisId: string, updates: Partial<InvestmentThesis>): Promise<InvestmentThesis> {
  const r = await api.put<InvestmentThesis>(`/theses/${thesisId}`, updates)
  return r.data
}

export async function deleteInvestmentThesis(thesisId: string): Promise<{ deleted: boolean; thesis_id: string }> {
  const r = await api.delete<{ deleted: boolean; thesis_id: string }>(`/theses/${thesisId}`)
  return r.data
}

export async function checkThesisDrift(
  thesisId: string,
  body: { current_metrics?: Record<string, any>; news_headlines?: string[] } = {},
): Promise<any> {
  const r = await api.post(`/theses/${thesisId}/drift-check`, body)
  return r.data
}

export async function evaluateBuffettChecklist(body: {
  symbol: string
  name?: string
  metrics?: Record<string, any>
}): Promise<BuffettChecklistResult> {
  const r = await api.post<BuffettChecklistResult>('/theses/checklist/evaluate', body)
  return r.data
}

export interface NewsPulseResult {
  symbol: string
  name: string
  price_change_pct: number
  volume_ratio: number
  nature: string
  nature_label: string
  urgency: string
  news_count: number
  news_samples: string[]
  thesis_impact: string
  thesis_alert?: string | null
  action_advice: string
}

export async function attributeNewsPulse(body: {
  symbol: string
  name?: string
  price_change_pct?: number
  volume_ratio?: number
  news_headlines?: string[]
}): Promise<NewsPulseResult> {
  const r = await api.post<NewsPulseResult>('/theses/pulse/attribute', body)
  return r.data
}

// ==================== Market Review ====================


export async function getMarketReview(market: 'cn' | 'hk' | 'us' | 'global' | 'crypto' | 'all', refresh = false): Promise<MarketReview> {
  // ALENS-175: 冷态现算实测 45s（暖态走缓存 <0.1s），默认 30s 首加载会超时
  const r = await api.get<MarketReview>(`/market-review/${market}`, { params: { refresh }, timeout: 90_000 })
  return r.data
}

// ==================== Emotion Cycle Compass (ALENS-119) ====================

export interface EmotionLadderLevel {
  board: number
  label: string
  count: number
  symbols: string[]
}

export interface EmotionPromotion {
  label: string
  base_count: number
  promoted: number
  rate: number
}

export interface EmotionCompass {
  as_of?: string | null
  limit_up_count: number
  broken_board_count: number
  limit_down_count: number
  touched_board_count: number
  seal_rate: number
  break_rate: number
  max_ladder: number
  ladder: EmotionLadderLevel[]
  promotions: EmotionPromotion[]
  open_premium_pct: number | null
  close_premium_pct: number | null
  nuke_rate: number
  emotion_score: number
  stage: string
  stage_en: 'freeze' | 'ignition' | 'ferment' | 'climax' | 'divergence' | 'ebb' | 'unknown'
  advice: string
  warnings: string[]
}

export async function getEmotionCompass(): Promise<EmotionCompass> {
  const r = await api.get<EmotionCompass>('/market/emotion-compass')
  return r.data
}

// ==================== Barra Style Radar (ALENS-120) ====================

export interface BarraStyleFactor {
  key: string
  label: string
  exposure: number | null
}

export interface BarraHoldingExposure {
  symbol: string
  weight: number
  size?: number | null
  beta?: number | null
  momentum?: number | null
  residual_vol?: number | null
  nonlinear_size?: number | null
  book_to_price?: number | null
  liquidity?: number | null
  earnings_yield?: number | null
  growth?: number | null
  leverage?: number | null
}

export interface BarraRadarResult {
  style_factors: BarraStyleFactor[]
  holdings: BarraHoldingExposure[]
  industry_exposure: Record<string, number>
  benchmark: Record<string, { momentum_pct?: number | null; annual_vol_pct?: number | null }>
  neutralize_available: boolean
  warnings: string[]
}

export async function fetchBarraRadar(
  symbols: string[],
  weights: Record<string, number>,
  lookbackDays = 400
): Promise<BarraRadarResult> {
  const r = await api.post<BarraRadarResult>('/portfolio/barra-radar', {
    symbols,
    weights,
    lookback_days: lookbackDays
  })
  return r.data
}

// ==================== Convertible Bond Workbench (ALENS-121) ====================

export interface CbondItem {
  symbol: string
  name: string
  price: number
  premium_pct: number | null
  double_low: number | null
  conv_price: number | null
  ytm: number | null
  pure_bond_premium_pct: number | null
  remain_year: number | null
  maturity: string
  stock_price?: number | null
  stock_symbol?: string | null
  implied_vol?: number | null
  coupon_rate?: number | null
}

export interface CbondWorkbench {
  count: number
  items: CbondItem[]
  skipped: number
  warnings: string[]
  detail_enriched?: number
}

export async function fetchCbondWorkbench(limit = 100): Promise<CbondWorkbench> {
  const r = await api.get<CbondWorkbench>('/cbond/workbench', { params: { limit } })
  return r.data
}

export interface CbondClauseTracker {
  symbol: string
  stock_symbol?: string | null
  price_source?: string
  window_days: number
  below_days: number
  below_progress: string
  downside_triggered: boolean
  above_days: number
  above_progress: string
  redemption_warned: boolean
  hist_vol: number | null
}

export async function fetchCbondClauseTracker(
  symbol: string,
  convPrice: number,
  stockSymbol?: string | null
): Promise<CbondClauseTracker> {
  const r = await api.get<CbondClauseTracker>('/cbond/clause-tracker', {
    params: {
      symbol,
      conv_price: convPrice,
      ...(stockSymbol ? { stock_symbol: stockSymbol } : {})
    }
  })
  return r.data
}

export interface CbondRotationBacktest {
  periods: number
  total_return_pct: number
  annualized_return_pct: number | null
  max_drawdown_pct: number
  holdings_history: { day: number; holdings: string[] }[]
  equity_curve: number[]
  warnings?: string[]
}

export async function runCbondBacktest(
  dailyDoubleLow: Record<string, (number | null)[]>,
  dailyPrices: Record<string, (number | null)[]>,
  rebalanceEvery = 20,
  topN = 10
): Promise<CbondRotationBacktest> {
  const r = await api.post<CbondRotationBacktest>('/cbond/backtest', {
    daily_double_low: dailyDoubleLow,
    daily_prices: dailyPrices,
    rebalance_every: rebalanceEvery,
    top_n: topN
  })
  return r.data
}

// ==================== Valuation Band & Reverse DCF (ALENS-122) ====================

/** 逆向 DCF 对比基准 (ALENS-194): 隐含增速 vs 营收 CAGR vs 行业均值 */
export interface ReverseDcfComparisons {
  implied_growth: number | null
  implied_growth_pct: number | null
  revenue_cagr_3y: number | null
  revenue_cagr_3y_pct: number | null
  industry_avg_growth: number | null
  industry_avg_growth_pct: number | null
  gap_vs_revenue_pct?: number | null
  gap_vs_industry_pct?: number | null
  note_vs_revenue?: string
}

export interface ReverseDcf {
  market_cap: number
  fcf_ttm: number
  wacc: number
  years: number
  terminal_growth: number
  implied_growth: number | null
  implied_growth_pct: number | null
  fair_value_at_zero_growth?: number | null
  verdict?: string
  available?: boolean
  reason?: string
  comparisons?: ReverseDcfComparisons
}

export interface ValuationBandReport {
  symbol: string
  pe_quantiles: Record<string, number>
  pb_quantiles: Record<string, number>
  pe_price_bands: Record<string, number | null>
  pb_price_bands: Record<string, number | null>
  pe_now: number | null
  pb_now: number | null
  pe_percentile: number | null
  pb_percentile: number | null
  reverse_dcf: ReverseDcf
  grid: { price: number; anchor: string; weight: number; note: string }[]
  warnings: string[]
}

export async function fetchValuationBand(symbol: string, years = 3): Promise<ValuationBandReport> {
  const r = await api.get<ValuationBandReport>(`/valuation/band/${encodeURIComponent(symbol)}`, {
    params: { years },
    timeout: 60_000,
  })
  return r.data
}

export async function computeReverseDcf(req: {
  market_cap: number
  fcf_ttm: number
  wacc?: number
  years?: number
  terminal_growth?: number
  symbol?: string
  industry_avg_growth?: number
}): Promise<ReverseDcf> {
  const r = await api.post<ReverseDcf>('/valuation/reverse-dcf', req)
  return r.data
}

// ==================== Forensic Accounting Radar (ALENS-123) ====================

export interface ForensicReport {
  symbol: string
  cash_debt?: {
    cash_ratio_pct: number | null
    debt_ratio_pct: number | null
    interest_yield_pct: number | null
    suspicious: boolean
    verdict: string
  }
  beneish?: {
    m_score: number
    suspicious: boolean
    factors: Record<string, number>
    missing_factors: string[]
  }
  intangible?: { ratio: number | null; level: string; note: string }
  chip?: {
    quarters: number
    holder_change_pct: number | null
    avg_value_change_pct: number | null
    signal: string
    detail: string
  }
  warnings: string[]
}

export async function fetchForensicReport(symbol: string): Promise<ForensicReport> {
  const r = await api.get<ForensicReport>(`/forensic/${encodeURIComponent(symbol)}`)
  return r.data
}

// ==================== Sector RRG Rotation (ALENS-124) ====================

export interface RrgTrailPoint {
  date: string
  rs_ratio: number
  rs_momentum: number
}

export interface RrgSector {
  sector: string
  rs_ratio: number
  rs_momentum: number
  quadrant: 'leading' | 'weakening' | 'lagging' | 'improving'
  trail: RrgTrailPoint[]
}

export interface CrowdingRow {
  sector: string
  share_pct: number | null
  percentile: number | null
  level: string
  note?: string
  amount?: number
}

export interface SectorRrgReport {
  as_of: string | null
  benchmark: string
  quadrant_labels: Record<string, string>
  sectors: RrgSector[]
  crowding: CrowdingRow[]
  warnings: string[]
}

export async function fetchSectorRrg(days = 750): Promise<SectorRrgReport> {
  // ALENS-175: 后端现算 24 行业×750 天历史实测 110s+（缓存冷启动），默认 30s 必然超时坏页
  const r = await api.get<SectorRrgReport>('/sector/rrg', { params: { days }, timeout: 150_000 })
  return r.data
}

// ==================== Margin Trading Monitor (ALENS-125) ====================

export interface MarginSentiment {
  latest_ratio_pct: number | null
  level: string
  note: string
  series: { date: string; net_buy_wan: number; ratio_pct: number }[]
}

export interface MarginDivergence {
  symbol: string
  name: string
  price_change_pct: number | null
  margin_net_sum_wan: number
  inflow_days: number
  signal: string
  detail: string
}

export interface MarginReport {
  sentiment: MarginSentiment
  divergences: MarginDivergence[]
  warnings: string[]
}

export async function fetchMarginMonitor(symbols?: string[]): Promise<MarginReport> {
  const r = await api.get<MarginReport>('/margin/monitor', {
    params: { symbols: symbols?.join(',') ?? '' }
  })
  return r.data
}

// ==================== Trade Behavior Analyzer (ALENS-126) ====================

export interface TradeBehaviorPoint {
  symbol: string
  entry_date: string
  exit_date: string
  pnl_pct: number
  mfe_pct: number | null
  mae_pct: number | null
  holding_days: number
}

export interface TradeBehaviorDiagnosis {
  win_rate_pct: number | null
  avg_win_pct: number | null
  avg_loss_pct: number | null
  payoff_ratio: number | null
  profit_factor: number | null
  avg_holding_win_days: number | null
  avg_holding_loss_days: number | null
  avg_mfe_pct: number | null
  avg_mae_pct: number | null
  mfe_capture_ratio: number | null
  disposition_effect: boolean
  loss_riding: boolean
  early_take_profit: boolean
  total_fee: number
  regime_stats: Record<string, { trades: number; win_rate_pct: number; avg_pnl_pct: number | null }>
  flags: string[]
}

export interface TradeBehaviorReport {
  trades: number
  winners: number
  losers: number
  points: TradeBehaviorPoint[]
  diagnosis: TradeBehaviorDiagnosis
  warnings: string[]
}

export async function analyzeTradeBehavior(req: {
  rows: Record<string, unknown>[]
  price_paths?: Record<string, Record<string, unknown>[]>
  regimes?: Record<string, string>
}): Promise<TradeBehaviorReport> {
  const r = await api.post<TradeBehaviorReport>('/trade-behavior/analyze', req)
  return r.data
}

// ==================== Portfolio ====================

export async function listPositions(): Promise<{ count: number; items: PortfolioPosition[] }> {
  const r = await api.get('/portfolio')
  return r.data
}

export async function listWatchlists(params?: { limit?: number }): Promise<any[]> {
  // 不再吞错返回 []: 之前失败与空数据不可区分, 调用方 (useUserStockPresets)
  // 自行 try/catch 并降级到默认标的
  const r = await api.get('/watchlist', { params })
  if (Array.isArray(r.data)) return r.data
  if (r.data && Array.isArray(r.data.items)) return r.data.items
  return []
}

export async function addToDefaultWatchlist(symbol: string): Promise<any> {
  const lists = await listWatchlists()
  let targetId = ''
  if (lists && lists.length > 0) {
    targetId = lists[0].id || lists[0].watchlist_id
  } else {
    const created = await api.post('/watchlist', {
      name: '默认自选',
      description: '多因子与截面选股加入',
      symbols: []
    })
    targetId = created.data.id || created.data.watchlist_id
  }
  const r = await api.post(`/watchlist/${targetId}/symbols`, { symbols: [symbol] })
  return r.data
}

export async function createWatchlist(body: { name: string; description?: string; symbols?: string[]; tags?: string[] }): Promise<any> {
  const r = await api.post('/watchlist', body)
  return r.data
}

export async function addSymbolsToWatchlist(watchlistId: string, symbols: string[]): Promise<any> {
  const r = await api.post(`/watchlist/${watchlistId}/symbols`, { symbols })
  return r.data
}

export async function removeSymbolsFromWatchlist(watchlistId: string, symbols: string[]): Promise<any> {
  const r = await api.delete(`/watchlist/${watchlistId}/symbols`, { data: { symbols } })
  return r.data
}

export async function deleteWatchlist(watchlistId: string): Promise<any> {
  const r = await api.delete(`/watchlist/${watchlistId}`)
  return r.data
}

export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  const r = await api.get('/portfolio/summary')
  return r.data
}

export async function addPosition(body: {
  symbol: string
  quantity: number
  cost_price: number
  purchase_date?: string
  cost_currency?: string
  notes?: string
}): Promise<PortfolioPosition> {
  const r = await api.post<PortfolioPosition>('/portfolio', body)
  return r.data
}

export async function updatePosition(id: string, body: {
  quantity?: number
  cost_price?: number
  notes?: string
}): Promise<PortfolioPosition> {
  const r = await api.put<PortfolioPosition>(`/portfolio/${id}`, body)
  return r.data
}

export async function deletePosition(id: string): Promise<{ deleted: boolean }> {
  const r = await api.delete<{ deleted: boolean }>(`/portfolio/${id}`)
  return r.data
}

export interface OptimizePortfolioRequest {
  symbols: string[]
  method: string
  risk_free_rate?: number
  period_days?: number
  include_frontier?: boolean
  current_weights?: Record<string, number>
}

export interface FrontierPoint {
  target_return: number
  volatility: number
  sharpe_ratio: number
  weights: Record<string, number>
}

export interface OptimizePortfolioResponse {
  optimization_id: string
  method: string
  success: boolean
  message: string
  weights: Record<string, number>
  expected_return: number
  volatility: number
  sharpe_ratio: number
  missing_symbols: string[]
  /** 因日期对齐(dropna)被静默丢弃的标的，数据不足或交易日历不匹配导致 */
  dropped_symbols?: string[]
  efficient_frontier?: FrontierPoint[]
  inefficient_frontier?: FrontierPoint[]
  full_frontier?: FrontierPoint[]
  gmv_portfolio?: FrontierPoint
  single_assets?: Array<{
    symbol: string
    expected_return: number
    volatility: number
    sharpe_ratio: number
  }>
  current_portfolio?: {
    expected_return: number
    volatility: number
    sharpe_ratio: number
    weights?: Record<string, number>
  }
}

export async function optimizePortfolio(body: OptimizePortfolioRequest): Promise<OptimizePortfolioResponse> {
  const r = await api.post('/portfolio/optimize', body)
  return r.data
}

export interface HolderNode {
  id: string
  label: string
  type: 'stock' | 'holder'
}

export interface HolderLink {
  source: string
  target: string
  weight: number
}

export interface HolderNetworkResponse {
  nodes: HolderNode[]
  links: HolderLink[]
}

export async function getHolderNetwork(symbols: string[]): Promise<HolderNetworkResponse> {
  const r = await api.post('/portfolio/holder-network', { symbols })
  return r.data
}

export interface GraphPathResponse {
  paths: Array<{
    nodes: Array<{ id: string; type: string; properties: Record<string, any> }>
    relationships: Array<{ type: string; start_node: string; end_node: string; properties: Record<string, any> }>
  }>
  fallback: boolean
}

export async function getHolderNetworkPath(source: string, target: string): Promise<GraphPathResponse> {
  const r = await api.get('/portfolio/holder-network/path', { params: { source, target } })
  return r.data
}

export async function triggerNeo4jSync(): Promise<{ success: boolean; message: string; task_id: string }> {
  const r = await api.post('/portfolio/holder-network/sync-neo4j')
  return r.data
}

export async function getNeo4jSyncStatus(): Promise<{
  status: string
  progress: number
  message: string
  is_syncing: boolean
  updated_at?: string
  error?: string
}> {
  const r = await api.get('/portfolio/holder-network/sync-neo4j/status')
  return r.data
}


export async function getNeo4jFullGraph(): Promise<{
  success: boolean
  scanned_at: string | null
  total_stocks: number
  total_nodes: number
  total_links: number
  neo4j_synced: boolean
  nodes: HolderNode[]
  links: HolderLink[]
}> {
  const r = await api.get('/portfolio/holder-network/neo4j-full')
  return r.data
}


// ==================== Alerts ====================

export async function listAlertRules(activeOnly = false): Promise<{ count: number; items: AlertRule[] }> {
  const r = await api.get('/alerts/rules', { params: { active_only: activeOnly } })
  return r.data
}

export async function addAlertRule(body: {
  symbol: string
  rule_type: string
  threshold?: number
  cooldown_seconds?: number
  channels?: string[]
  notes?: string
}): Promise<AlertRule> {
  const r = await api.post<AlertRule>('/alerts/rules', body)
  return r.data
}

export async function deleteAlertRule(id: string): Promise<{ deleted: boolean }> {
  const r = await api.delete<{ deleted: boolean }>(`/alerts/rules/${id}`)
  return r.data
}

export async function toggleAlertRule(id: string, active: boolean): Promise<AlertRule> {
  const r = await api.put<AlertRule>(`/alerts/rules/${id}/toggle`, null, { params: { active } })
  return r.data
}

export async function listAlertHistory(limit = 50): Promise<{ count: number; items: AlertHistory[] }> {
  const r = await api.get('/alerts/history', { params: { limit } })
  return r.data
}

export async function runAlertCheck(): Promise<{ triggered_count: number; triggered: any[] }> {
  const r = await api.post('/alerts/check')
  return r.data
}

// ==================== Batch ====================

export interface BatchItemSummary {
  analysis_id: string
  query: string
  status: string
  decision?: {
    decision_type: string
    confidence: number
    stock_name?: string
    symbol?: string
    core_conclusion?: string
    sniper_levels?: { entry?: number; target_1?: number }
  }
  error?: string
  completed_at?: string
}

export interface Batch {
  batch_id: string
  name: string
  status: string
  depth: string
  queries: string[]
  analysis_ids: string[]
  created_at: string
  completed_at: string | null
  total: number
  pending: number
  running: number
  completed: number
  failed: number
  items: BatchItemSummary[]
}

export async function createBatch(body: {
  queries: string[]
  depth?: AnalysisDepth
  name?: string
}): Promise<{
  batch_id: string
  status: string
  total: number
  pending: number
  running: number
  completed: number
  failed: number
  created_at: string
  name: string
}> {
  const r = await api.post('/batch', { depth: 'standard', ...body })
  return r.data
}

export async function getBatch(id: string): Promise<Batch> {
  const r = await api.get(`/batch/${id}`)
  return r.data
}

export async function retryBatch(id: string): Promise<{ retried_count: number }> {
  const r = await api.post(`/batch/${id}/retry`)
  return r.data
}

export async function deleteBatch(id: string): Promise<{ deleted: boolean; decisions_removed: number }> {
  const r = await api.delete(`/batch/${id}`)
  return r.data
}

export async function retryAnalysis(id: string): Promise<{ retried: boolean }> {
  const r = await api.post(`/analyze/${id}/retry`)
  return r.data
}

export async function listBatches(limit = 20): Promise<{ count: number; items: Batch[] }> {
  const r = await api.get('/batch', { params: { limit } })
  return r.data
}

// ==================== Chat ====================

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  /** RAG 引用来源 (仅 assistant 消息且命中知识库时返回) */
  sources?: Array<{ title: string; score: number; content?: string }>
}

export interface ChatSession {
  session_id: string
  user: string
  symbol: string | null
  title: string
  messages: ChatMessage[]
  created_at: string
  updated_at: string
  metadata: Record<string, any>
  message_count?: number
}

export interface ChatSessionSummary {
  session_id: string
  user: string
  symbol: string | null
  title: string
  message_count: number
  created_at: string
  updated_at: string
}

export interface ChatResponse {
  session_id: string
  user_message: ChatMessage
  assistant_message: ChatMessage
  tokens_estimated: number
}

export async function createChatSession(body: {
  symbol?: string
  title?: string
  first_message?: string
}): Promise<{
  session_id: string
  symbol: string | null
  title: string
  created_at: string
}> {
  const r = await api.post('/chat/sessions', body)
  return r.data
}

export async function listChatSessions(limit = 50): Promise<{
  count: number
  items: ChatSessionSummary[]
}> {
  const r = await api.get('/chat/sessions', { params: { limit } })
  return r.data
}

export async function getChatSession(id: string): Promise<ChatSession> {
  const r = await api.get(`/chat/sessions/${id}`)
  return r.data
}

export async function deleteChatSession(id: string): Promise<{ deleted: boolean }> {
  const r = await api.delete(`/chat/sessions/${id}`)
  return r.data
}

export async function postChatMessage(body: {
  session_id: string
  message: string
}): Promise<ChatResponse> {
  const r = await api.post('/chat/messages', body)
  return r.data
}

export async function attachChatAnalysis(
  sessionId: string,
  analysisId: string,
): Promise<{ attached: boolean; analysis_id: string }> {
  const r = await api.post(`/chat/sessions/${sessionId}/attach-analysis`, {
    analysis_id: analysisId,
  })
  return r.data
}

export interface FactorDefinition {
  factor_id: string
  name: string
  category: string
  description: string
  formula?: string
  params?: Record<string, any>
  direction: 'positive' | 'negative'
  user?: string
  builtin?: boolean
  created_at?: string
}

export interface FactorEvalResult {
  eval_id?: string
  factor_id: string
  factor_name: string
  period_start: string
  period_end: string
  ic_mean: number
  ic_std: number
  ic_ir: number
  sharpe_ratio: number
  quantile_returns: Record<string, number | number[]>
  created_at: string
}

export async function listFactors(includeBuiltin = true): Promise<{
  count: number
  items: FactorDefinition[]
}> {
  const r = await api.get('/factors/list', { params: { include_builtin: includeBuiltin } })
  return r.data
}

export async function getFactor(factorId: string): Promise<FactorDefinition> {
  const r = await api.get(`/factors/${factorId}`)
  return r.data
}

export async function createFactor(body: {
  name: string
  category: string
  description?: string
  formula: string
  params?: Record<string, any>
  direction?: 'positive' | 'negative'
}): Promise<FactorDefinition> {
  const r = await api.post('/factors', body)
  return r.data
}

export async function evaluateFactor(body: {
  factor_id: string
  symbols: string[]
  period_days?: number
  forward_days?: number
  quantiles?: number
  save?: boolean
}): Promise<{
  eval_id: string | null
  ic_mean: number
  ic_std: number
  ic_ir: number
  sharpe_ratio: number
  quantile_returns: Record<string, number | number[]>
  loaded_symbols: string[]
  missing_symbols: string[]
}> {
  const r = await api.post('/factors/evaluate', body)
  return r.data
}

export async function listFactorEvals(factorId?: string, limit = 50): Promise<{
  count: number
  items: FactorEvalResult[]
}> {
  const r = await api.get('/factors/eval/list', { params: { factor_id: factorId, limit } })
  return r.data
}

// ==================== Screener (自动选股) ====================

export interface ScreenerIndex {
  code: string
  name: string
}

export interface ScreenRequest {
  index_code: string
  pe_min?: number | null
  pe_max?: number | null
  pb_min?: number | null
  pb_max?: number | null
  roe_min?: number | null
  market_cap_min?: number | null
  market_cap_max?: number | null
  net_inflow_5d_min?: number | null
  industry_keywords?: string[] | null
}

export interface ScreenedStock {
  symbol: string
  stock_name: string
  pe_ttm: number | null
  pb: number | null
  roe: number | null
  market_cap_bn: number | null
  net_inflow_5d: number | null
  pct_chg: number | null
  close: number | null
  industry: string
}

export interface ScreenResponse {
  success: boolean
  count: number
  data: ScreenedStock[]
}

export interface AIScreenResponse {
  success: boolean
  params: Record<string, any>
  count: number
  stocks: ScreenedStock[]
  report: string
}

export async function getScreenerIndices(): Promise<ScreenerIndex[]> {
  const r = await api.get('/screener/indices')
  return r.data
}

export async function screenStocks(body: ScreenRequest): Promise<ScreenResponse> {
  const r = await api.post('/screener/screen', body)
  return r.data
}

export async function runAIScreen(prompt: string): Promise<AIScreenResponse> {
  const r = await api.post('/screener/ai-screen', { prompt })
  return r.data
}

// ==================== Enriched Stats ====================

export interface EnrichedStockStats {
  symbol: string
  stock_name: string
  close: number | null
  pct_chg: number | null
  pe_ttm: number | null
  pb: number | null
  roe: number | null
  volatility: number | null
  sharpe_ratio: number | null
  max_drawdown: number | null
  industry: string
}

export async function getEnrichedStats(symbols: string[], periodDays = 252): Promise<{
  success: boolean
  data: Record<string, EnrichedStockStats>
}> {
  const r = await api.post('/screener/enrich-stats', { symbols, period_days: periodDays })
  return r.data
}

// ==================== Fundamental Metrics (ALENS-129) ====================
// 单标的最新财报基本面: 漂移检测红线与六关检查的指标来源。
// 全部由同量纲原始字段派生 (比率字段为百分比), 缺数据时为 null。

export interface FundamentalMetrics {
  gross_margin: number | null
  net_margin: number | null
  debt_ratio: number | null
  current_ratio: number | null
  cfo: number | null
  cfo_to_net_profit: number | null
  report_date: string | null
}

export async function getFundamentals(symbols: string[]): Promise<{
  success: boolean
  data: Record<string, FundamentalMetrics>
}> {
  const r = await api.post('/screener/fundamentals', { symbols })
  return r.data
}

// ==================== General Settings ====================

export interface GeneralSettings {
  risk_free_rate: number
  updated_at: string
}

export async function getGeneralSettings(): Promise<GeneralSettings> {
  const r = await api.get('/system/general')
  return r.data
}

export async function updateGeneralSettings(riskFreeRate: number): Promise<GeneralSettings> {
  const r = await api.put('/system/general', { risk_free_rate: riskFreeRate })
  return r.data
}

export async function getLatestAnomalyScan(): Promise<{
  success: boolean
  scanned_at: string | null
  total_scanned: number
  count: number
  anomalies: any[]
  anomaly_map: Record<string, any>
}> {
  const r = await api.get('/screener/anomalies/latest')
  return r.data
}

// ==================== Portfolio Diagnosis & Quant Analysis ====================

export interface RebalanceSuggestion {
  symbol: string
  stock_name: string
  action: '加仓' | '减仓' | '清仓/止损' | '观望'
  target_weight_change: string
  reason: string
}

export interface PortfolioDiagnosisResponse {
  success: boolean
  /** 任务轮询响应携带 status/error; 完成态响应无此字段 */
  status?: string
  error?: string
  overall_score: number
  health_status: string
  report_md: string
  rebalance_suggestions: RebalanceSuggestion[]
  quant_metrics: {
    // 后端基于真实 K 线计算; 样本不足 (<30 交易日) 时为 null, 不再伪造数值
    sharpe_ratio: number | null
    sortino_ratio: number | null
    max_drawdown: number | null
    var_95: number | null
    cvar_95: number | null
    volatility: number | null
    stock_hhi: number
    sector_hhi: number
  }
  factor_attribution: {
    // momentum/low_volatility 由真实数据计算; value/quality/size 未接入基本面数据源, 为 null
    momentum: number | null
    value: number | null
    quality: number | null
    low_volatility: number | null
    size: number | null
  }
  anomalies: Array<{
    symbol: string
    stock_name: string
    anomalies: string[]
    level: 'info' | 'warning' | 'danger'
  }>
  scanned_at: string
  data_source?: string
  warnings?: string[]
}

export async function getPortfolioDiagnosis(): Promise<PortfolioDiagnosisResponse> {
  const r = await api.get('/portfolio/diagnose', { timeout: 90_000 })
  return r.data
}

export async function createPortfolioDiagnosis(): Promise<{ success: boolean; task_id: string; status: string }> {
  const r = await api.post('/portfolio/diagnose')
  return r.data
}

export async function getLatestPortfolioDiagnosis(): Promise<PortfolioDiagnosisResponse> {
  const r = await api.get('/portfolio/diagnose/latest')
  return r.data
}

export async function getPortfolioDiagnosisById(taskId: string): Promise<PortfolioDiagnosisResponse> {
  const r = await api.get(`/portfolio/diagnose/${taskId}`)
  return r.data
}


export async function getPortfolioAnomalies(): Promise<{
  success: boolean
  count: number
  items: Array<{
    symbol: string
    stock_name: string
    anomalies: string[]
    level: 'info' | 'warning' | 'danger'
  }>
}> {
  const r = await api.get('/portfolio/anomalies', { timeout: 30_000 })
  return r.data
}

export interface PortfolioQuantMetricsResponse {
  metrics: {
    sharpe_ratio: number | null
    sortino_ratio: number | null
    max_drawdown: number | null
    var_95: number | null
    cvar_95: number | null
    volatility: number | null
    stock_hhi: number | null
    sector_hhi: number | null
    [key: string]: number | null | undefined
  }
  factor_attribution: {
    momentum: number | null
    value: number | null
    quality: number | null
    low_volatility: number | null
    size: number | null
    [key: string]: number | null | undefined
  }
  position_weights: Array<{ symbol: string; stock_name: string; weight: number; pnl_pct: number; current_value: number }>
}

export async function getPortfolioQuantMetrics(): Promise<PortfolioQuantMetricsResponse> {
  const r = await api.get('/portfolio/quant-metrics', { timeout: 30_000 })
  return r.data
}

export interface PortfolioHoldingBacktestPoint {
  date: string
  portfolio?: number
  portfolio_equity?: number
  portfolio_return?: number
  equity?: number
  benchmark?: number
  benchmark_equity?: number
  benchmark_return?: number
  excess_return?: number
}

export async function runPortfolioHoldingBacktest(periodDays = 180): Promise<{
  success: boolean
  error?: string
  warnings?: string[]
  metrics: Record<string, number>
  equity_curve: PortfolioHoldingBacktestPoint[]
}> {
  const r = await api.post('/portfolio/backtest-holdings', { period_days: periodDays }, { timeout: 90_000 })
  return r.data
}

export type PortfolioTradeType = 'buy' | 'sell' | 'swap'

export interface PortfolioTransaction {
  _id: string
  type?: PortfolioTradeType
  trade_type?: PortfolioTradeType
  type_name?: string
  symbol: string
  stock_name: string
  quantity: number
  price: number
  total_amount?: number
  realized_pnl?: number
  trade_date: string
  notes?: string
}

export async function executePortfolioTrade(body: {
  trade_type: PortfolioTradeType
  symbol: string
  quantity: number
  price: number
  trade_date?: string
  notes?: string
  target_symbol?: string
  target_price?: number
}): Promise<any> {
  const r = await api.post('/portfolio/trade', body)
  return r.data
}

export async function listPortfolioTransactions(limit = 100): Promise<{
  count: number
  total_realized_pnl: number
  items: PortfolioTransaction[]
}> {
  const r = await api.get('/portfolio/transactions', { params: { limit } })
  return r.data
}

export async function amendPortfolioTransaction(
  transactionId: string,
  body: {
    symbol?: string
    price?: number
    quantity?: number
    trade_date?: string
    notes?: string
  }
): Promise<any> {
  const r = await api.put(`/portfolio/transactions/${transactionId}`, body)
  return r.data
}

export async function deletePortfolioTransaction(transactionId: string): Promise<any> {
  const r = await api.delete(`/portfolio/transactions/${transactionId}`)
  return r.data
}

export async function rebuildPortfolioFromTransactions(): Promise<any> {
  const r = await api.post('/portfolio/rebuild-from-transactions')
  return r.data
}


export interface FamaFrenchAttributionData {
  alpha: number | null
  beta_market: number | null
  beta_smb: number | null
  beta_hml: number | null
  beta_rmw: number | null
  beta_cma: number | null
  r_squared: number | null
  explained_by_factors_pct: number | null
  data_source?: string
}

export interface StressTestScenarioData {
  scenario_id: string
  name: string
  period: string
  description: string
  market_benchmark_drop: number
  estimated_portfolio_drop: number
  estimated_recovery_days: number
  risk_level: 'high' | 'medium' | 'low'
  mitigation_advice: string
}

export interface InstitutionalQuantResponse {
  success: boolean
  fama_french_5factor: FamaFrenchAttributionData
  institutional_metrics: {
    information_ratio: number | null
    treynor_ratio: number | null
    jensen_alpha: number | null
    omega_ratio: number | null
    tail_risk_ratio: number | null
    tracking_error: number | null
    data_source?: string
  }
  stress_test_scenarios: StressTestScenarioData[]
  brinson_attribution: {
    allocation_effect: number | null
    selection_effect: number | null
    interaction_effect: number | null
    total_active_return: number | null
    data_source?: string
  }
  scanned_at?: string
  data_source?: string
}

export async function getTasksSummary(limit = 50): Promise<{
  success: boolean
  kpi: { running: number; completed: number; failed: number; total: number }
  items: Array<{
    id: string
    type: string
    type_label: string
    name: string
    status: 'running' | 'completed' | 'failed' | 'idle'
    progress: number
    created_at: string
    completed_at?: string
    detail?: string
    target_link?: string
  }>
}> {
  const r = await api.get('/tasks/summary', { params: { limit } })
  return r.data
}


export async function getPortfolioInstitutionalQuant(): Promise<InstitutionalQuantResponse> {
  const r = await api.get('/portfolio/institutional-quant', { timeout: 90_000 })
  return r.data
}

export interface TechnicalAnalysisResponse {
  symbol: string
  stock_name: string
  days: number
  kline: Array<{
    date: string
    open: number
    high: number
    low: number
    close: number
    volume: number
    pct_chg: number
  }>
  indicators: {
    ma: { ma5: (number | null)[]; ma10: (number | null)[]; ma20: (number | null)[]; ma60: (number | null)[] }
    bollinger: { bbm: (number | null)[]; bbu: (number | null)[]; bbl: (number | null)[] }
    fibonacci: { min_price: number; max_price: number; fib_236: number; fib_382: number; fib_500: number; fib_618: number }
    macd: { dif: number[]; dea: number[]; macd_bar: number[] }
    obv: number[]
    kdj: { k: number[]; d: number[]; j: number[] }
    rsi: { rsi6: number[]; rsi12: number[]; rsi24: number[] }
    emv: { emv: number[]; maemv: number[] }
    dmi: { adx: number[]; pdi: number[]; mdi: number[] }
    signals: Array<{
      index: number
      date: string
      type: 'buy' | 'sell'
      label: string
      price: number
      indicator: string
    }>
  }
}

export async function getStockTechnicalAnalysis(symbol: string, days = 150): Promise<TechnicalAnalysisResponse> {
  const r = await api.get(`/portfolio/technical-analysis/${symbol}`, { params: { days } })
  return r.data
}

export interface PairTradingResponse {
  symbol_x: string
  name_x: string
  symbol_y: string
  name_y: string
  regression: {
    beta: number
    alpha: number
    r_squared: number
    correlation: number
    std_err: number
    ols_beta: number
    kalman_beta_latest: number
  }
  cointegration?: {
    is_cointegrated: boolean
    adf_t_stat: number
    adf_p_value: number
    ou_half_life_days: number
    assessment: string
  }
  spread_stats: {
    mean: number
    std: number
    upper_bound: number
    lower_bound: number
  }
  time_series: Array<{
    date: string
    price_x: number
    price_y: number
    spread: number
    z_score: number
  }>
  signals: Array<{
    date: string
    type: string
    label: string
    z_score: number
    spread: number
    advice: string
  }>
}

export async function analyzePairTrading(symbol_x: string, symbol_y: string, days = 180): Promise<PairTradingResponse> {
  const r = await api.post('/portfolio/pair-trading/analyze', { symbol_x, symbol_y, days })
  return r.data
}

export interface MarketCorrelationResponse {
  latest_market_correlation: number
  risk_status: string
  risk_level: string
  sectors: Array<{
    name: string
    code: string
    symbol: string
    annualized_return: number
    annualized_volatility: number
    max_drawdown: number
    correlation_with_market: number | null
  }>
  history: Array<{
    date: string
    avg_correlation: number
    market_return_pct: number
  }>
  data_source: string
  sectors_loaded: number
}

export async function getMarketCorrelation(): Promise<MarketCorrelationResponse> {
  const r = await api.get('/portfolio/market-correlation')
  return r.data
}

export interface CppiSimulationResponse {
  symbol: string
  stock_name: string
  params: {
    start_value: number
    multiplier: number
    floor_pct: number
    drawdown_limit?: number
    riskfree_rate: number
  }
  performance: {
    cppi_final_value: number
    cppi_return_pct: number
    cppi_max_drawdown: number
    risky_final_value: number
    risky_return_pct: number
    risky_max_drawdown: number
    capital_protected: boolean
  }
  trajectory: Array<{
    date: string
    cppi_value: number
    risky_value: number
    cushion_pct: number
    risky_weight_pct: number
  }>
}

export async function simulateCppi(body: {
  symbol?: string
  start_value?: number
  multiplier?: number
  floor_pct?: number
  riskfree_rate?: number
  drawdown_limit?: number
  days?: number
}): Promise<CppiSimulationResponse> {
  const r = await api.post('/portfolio/cppi/simulate', body)
  return r.data
}

export interface MonteCarloResponse {
  params: {
    initial_capital: number
    years: number
    scenarios: number
    expected_mu: number
    volatility_sigma: number
    target_goal: number
    floor_limit: number
  }
  stats: {
    mean_wealth: number
    median_wealth: number
    p10_pessimistic: number
    p90_optimistic: number
    success_rate: number
    shortfall_risk_rate: number
  }
  trajectory: Array<{
    month: number
    year: number
    p10: number
    p25: number
    p50_median: number
    p75: number
    p90: number
  }>
}

export async function simulateMonteCarlo(body: {
  initial_capital?: number
  years?: number
  scenarios?: number
  mu?: number
  sigma?: number
  target_goal?: number
  floor_limit?: number
}): Promise<MonteCarloResponse> {
  const r = await api.post('/portfolio/monte-carlo/simulate', body)
  return r.data
}

export interface BlackLittermanResponse {
  params: { risk_aversion: number; tau: number; num_assets: number; num_user_views: number }
  allocations: Array<{
    symbol: string
    name: string
    capm_prior_return_pct: number
    bl_posterior_return_pct: number
    user_view_pct?: number
    prior_weight_pct: number
    bl_optimal_weight_pct: number
  }>
}

export async function runBlackLitterman(body?: {
  symbols?: string[]
  views?: Record<string, number>
  risk_aversion?: number
  tau?: number
}): Promise<BlackLittermanResponse> {
  const r = await api.post('/portfolio/black-litterman', body || {})
  return r.data
}

export interface HrpResponse {
  num_assets: number
  method: string
  allocations: Array<{
    symbol: string
    name: string
    hrp_weight_pct: number
    ivp_weight_pct: number
    ew_weight_pct: number
  }>
}

export async function runHrpOptimize(symbols?: string[]): Promise<HrpResponse> {
  const r = await api.post('/portfolio/hrp-optimize', { symbols })
  return r.data
}

export interface QuantBacktestResponse {
  symbol: string
  stock_name: string
  strategy_code: string
  strategy_label: string
  performance: {
    initial_capital: number
    final_equity: number
    total_return_pct: number
    benchmark_return_pct: number
    alpha_excess_pct: number
    total_trades_count: number
    win_rate_pct: number
    sharpe_ratio?: number
    max_drawdown_pct?: number
    profit_loss_ratio?: number
    /** 零回撤时无定义, 后端返回 null */
    calmar_ratio?: number | null
  }

  equity_curve: Array<{
    date: string
    equity: number
    benchmark_equity: number
    position_shares: number
  }>
  trades_history: Array<{
    date: string
    type: 'BUY' | 'SELL'
    price: number
    shares: number
    commission: number
    cash_after: number
  }>
}

export async function runQuantBacktest(body: {
  symbol?: string
  strategy_name?: string
  initial_capital?: number
  commission_rate?: number
  days?: number
}): Promise<QuantBacktestResponse> {
  const r = await api.post('/portfolio/quant-trading/backtest', body)
  return r.data
}

// ==================== Phase 1: 单标的深度研究 ====================

/** 财报抽取: 上传 PDF/Excel → 关键字段 */
export interface ParsedReportField {
  value: string | number | null
  unit?: string
  raw_match?: string
  confidence?: number
}

export interface ParseReportResponse {
  filename?: string
  symbol?: string
  stock_name?: string
  report_period?: string
  publish_date?: string
  success: boolean
  source_type: string
  fields: Record<string, ParsedReportField>
  warnings: string[]
}

export async function parseFinancialReport(file: File): Promise<ParseReportResponse> {
  const fd = new FormData()
  fd.append('file', file)
  const r = await api.post('/report/parse', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  })
  return r.data
}

export async function extractOnlineFinancialReport(symbol: string): Promise<ParseReportResponse> {
  const r = await api.get('/report/extract-online', {
    params: { symbol },
    timeout: 30_000,
  })
  return r.data
}

/** 标的舆情汇总 */
export interface SentimentNewsItem {
  title: string
  score: number
  label: string
  confidence: number
  published_at?: string
}

export interface SymbolSentimentResponse {
  symbol: string
  period_days: number
  news_count: number
  source?: string
  summary: {
    overall_label?: string
    overall_score?: number
    positive_count?: number
    negative_count?: number
    neutral_count?: number
    total?: number
    positive?: number
    negative?: number
    neutral?: number
  } | Record<string, never>
  top_positive?: SentimentNewsItem[]
  top_negative?: SentimentNewsItem[]
  items: (SentimentNewsItem & { reasoning?: string; source?: string })[]
  note?: string
  alphascout_raw?: {
    sentiment_label?: string
    sentiment_score?: number
    time_window_hours?: number
    updated_at?: string
  }
}

export async function symbolSentiment(symbol: string, days = 7): Promise<SymbolSentimentResponse> {
  const r = await api.post('/sentiment/symbol', { symbol, days })
  return r.data
}

// ==================== AlphaScout 舆情平台 API ====================

export interface AlphaScoutInsight {
  message_id?: string
  source_type?: string
  summary: string
  sentiment_score: number
  sentiment_kind?: string
  reasoning?: string
  published_at?: string
}

/** 流动性面板 (单标的微观流动性) */
export interface LiquidityMetrics {
  symbol: string
  name: string
  avg_daily_volume: number
  avg_daily_amount: number
  avg_turnover_rate: number
  turnover_volatility: number
  amihud_illiq: number
  roll_spread: number
  corwin_schultz_spread: number
  kyles_lambda: number
  liquidity_score: number
  liquidity_rating: string
}

export async function analyzeLiquidity(symbols: string[], window = 20): Promise<LiquidityMetrics[]> {
  const r = await api.post('/liquidity/analyze', { symbols, window })
  if (Array.isArray(r.data)) return r.data
  if (r.data?.data && Array.isArray(r.data.data)) return r.data.data
  return [r.data]
}

export interface LVarStressTestRequest {
  portfolio_value?: number
  symbols: string[]
  weights?: Record<string, number>
  confidence_level?: number
  liquidation_days?: number
  max_daily_adv_pct?: number
}

export interface LiquidationScheduleDay {
  day: number
  liquidated_amount: number
  remaining_amount: number
  estimated_slippage_pct: number
  haircut_cost: number
}

export interface LVarStressTestResponse {
  success: boolean
  portfolio_value: number
  confidence_level: number
  base_var_pct: number
  base_var_amount: number
  l_var_pct: number
  l_var_amount: number
  liquidation_haircut_pct: number
  liquidation_days: number
  liquidation_schedule: LiquidationScheduleDay[]
}

export async function testLVarStress(req: LVarStressTestRequest): Promise<LVarStressTestResponse> {
  const r = await api.post('/liquidity/l_var_stress_test', req)
  return r.data
}

export interface CapacityLevelItem {
  capital: number
  capital_label: string
  annualized_return: number
  sharpe_ratio: number
  max_drawdown: number
  total_slippage_cost: number
  avg_slippage_pct: number
  capacity_retention_rate: number
}

export interface CapacityBacktestResponse {
  success: boolean
  benchmark_capital: number
  base_annual_return: number
  base_sharpe: number
  optimal_capacity: number
  capacity_levels: CapacityLevelItem[]
}

export interface CapacityBacktestRequest {
  base_annual_return?: number
  base_sharpe?: number
  base_max_dd?: number
  avg_adv?: number
  annual_turnover_times?: number
  capital_levels?: number[]
}

export async function backtestLiquidityCapacity(
  req: CapacityBacktestRequest = {}
): Promise<CapacityBacktestResponse> {
  const r = await api.post('/liquidity/capacity_backtest', req)
  return r.data
}

// ------------------------------------------------------------- 期权定价与组合策略 (ALENS-118)

export interface OptionGreeks {
  delta: number
  gamma: number
  theta: number
  vega: number
  rho: number
}

export interface OptionPriceRequest {
  option_type: 'call' | 'put'
  underlying?: string
  strike: number
  spot: number
  expiry_days: number
  risk_free_rate?: number
  volatility?: number
}

export interface OptionPriceResponse {
  option_type: 'call' | 'put'
  underlying: string
  spot: number
  strike: number
  expiry_days: number
  volatility: number
  price: number
  intrinsic: number
  time_value: number
  greeks: OptionGreeks
}

export async function priceOption(req: OptionPriceRequest): Promise<OptionPriceResponse> {
  const r = await api.post('/options/price', req)
  return r.data
}

export interface OptionIVRequest {
  option_type: 'call' | 'put'
  market_price: number
  spot: number
  strike: number
  expiry_days: number
  risk_free_rate?: number
}

export interface OptionIVResponse {
  option_type: 'call' | 'put'
  market_price: number
  implied_volatility: number
  implied_volatility_pct: string
  model_price_at_iv: number
  convergence_error: number
}

export async function calcOptionIV(req: OptionIVRequest): Promise<OptionIVResponse> {
  const r = await api.post('/options/implied-volatility', req)
  return r.data
}

export interface OptionStrategyResult {
  name: string
  strategy_type: string
  net_premium: number
  max_profit: number | null
  max_loss: number | null
  breakeven_points: number[]
  profit_zones: unknown[]
  payoff_at_spot: number | null
  description: string
  legs?: {
    option_type: string
    side: string
    strike: number
    premium: number
    quantity: number
  }[]
}

export async function analyzeOptionStrategy(
  strategy:
    | 'covered-call'
    | 'bull-call-spread'
    | 'straddle'
    | 'strangle'
    | 'butterfly'
    | 'protective-put',
  params: Record<string, any>
): Promise<OptionStrategyResult> {
  const r = await api.post(`/options/strategy/${strategy}`, params)
  return r.data
}

export interface OptionPayoffPoint {
  /** 到期标的价格 */
  underlying: number
  /** 该价位盈亏 (服务端曲线键为 payoff; 客户端曲线在组装时映射为 pnl) */
  payoff: number
}

export interface OptionPayoffResponse {
  spot: number
  curve_points: number
  payoff_curve: OptionPayoffPoint[]
}

export async function getOptionPayoffCurve(
  params: Record<string, any>
): Promise<OptionPayoffResponse> {
  const r = await api.post('/options/payoff-curve', params)
  return r.data
}

/** RAG 知识问答 (检索自己的历史决策 + 静态知识) */
export interface RagSource {
  id: string
  title: string
  source: string
  score: number
}

export interface RagAskResponse {
  query: string
  answer: string
  sources: RagSource[]
  retrieved_count: number
}

export async function ragAsk(query: string, opts: { use_llm?: boolean; symbol?: string } = {}): Promise<RagAskResponse> {
  const r = await api.post('/rag/ask', {
    query,
    use_llm: opts.use_llm ?? true,
    include_decisions: true,
    include_knowledge: true,
    symbol: opts.symbol || null,
  })
  return r.data
}

// ==================== Phase 2: 组合与风控治理 ====================

/** 风险快照 */
export interface PositionItem {
  symbol: string
  shares: number
  cost_price: number
}

export interface PortfolioRiskRequest {
  positions: PositionItem[]
  benchmark?: string
  period_days?: number
}

export interface RiskSnapshot {
  snapshot_id: string
  metrics: Record<string, number>
  positions?: any[]
  created_at?: string
}

export async function runRiskSnapshot(req: PortfolioRiskRequest): Promise<RiskSnapshot> {
  const r = await api.post('/risk/portfolio', req)
  return r.data
}

export async function listRiskSnapshots(limit = 20): Promise<RiskSnapshot[]> {
  const r = await api.get('/risk/portfolio/snapshots', { params: { limit } })
  return r.data.items || []
}

/** 决策回测 (基于已有 analysis 的决策仪表盘) */
export interface DecisionBacktestResult {
  backtest_id: string
  analysis_id: string
  symbol: string
  period: string
  metrics: Record<string, number>
  trade_count: number
  equity_curve_points: number
  /** 净值曲线 —— 只有详情端点 (GET /backtest/{id}) 带; 发起响应仅给点数 */
  equity_curve?: { equity: number; ts_event?: number; date?: string }[]
}

/** 决策回测详情 (含净值曲线, 供 HistoryView 弹窗画图) */
export async function getDecisionBacktest(backtestId: string): Promise<DecisionBacktestResult> {
  const r = await api.get(`/backtest/${backtestId}`)
  return r.data
}

export async function backtestSingle(body: {
  analysis_id: string
  period?: string
  initial_capital?: number
}): Promise<DecisionBacktestResult> {
  const r = await api.post('/backtest/single', body, { timeout: 60_000 })
  return r.data
}

/** 策略回测历史 */
export interface StrategyBacktestSummary {
  run_id: string
  strategy_type: string
  symbols: string[]
  benchmark?: string
  period_days: number
  initial_capital: number
  metrics: Record<string, number>
  created_at: string
}

export async function listStrategyBacktests(limit = 20): Promise<StrategyBacktestSummary[]> {
  const r = await api.get('/strategy-backtest/list', { params: { limit } })
  return r.data.items || []
}

/** 组合策略回测入参 (与后端 StrategyBacktestRequest 对齐) */
export interface StrategyBacktestRunPayload {
  strategy_type: string
  symbols: string[]
  target_weights?: Record<string, number>
  benchmark?: string
  frequency?: string
  initial_capital?: number
  period_days?: number
  drift_threshold?: number
  commission_rate?: number
  save_snapshot?: boolean
}

/**
 * 运行组合策略回测 (G8/ALENS-80)。
 *
 * 组合回测是同步 CPU 计算: 回测天数拉满 (1260 天 × 多标的) 时会超过 axios
 * 默认 30s —— 前端先超时报错而后端还在算, 用户看到的是假失败。这里把
 * 超时提到 120s, 且必须**大于**后端 90s 计算预算: 后端要么在预算内返回,
 * 要么显式 504, 前端两种情况都能拿到确定终态。
 */
export async function runStrategyBacktest(payload: StrategyBacktestRunPayload): Promise<any> {
  const r = await api.post('/strategy-backtest/run', payload, { timeout: 120_000 })
  return r.data
}

/** MPT 优化历史 */
export interface OptimizationSummary {
  optimization_id: string
  method?: string
  created_at: string
  metrics?: Record<string, number>
  [k: string]: any
}

export async function listOptimizations(limit = 20): Promise<OptimizationSummary[]> {
  const r = await api.get('/portfolio/optimization/list', { params: { limit } })
  return r.data.items || []
}

/** 数据导出 (blob 下载; GET 需带 Authorization, 不能用 <a href>) */
export async function downloadExport(dataType: string, format: 'csv' | 'json' | 'excel' = 'csv'): Promise<void> {
  const r = await api.get(`/export/${dataType}`, {
    params: { format },
    responseType: 'blob',
    timeout: 120_000,
  })
  const url = URL.createObjectURL(r.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `alphalens_${dataType}_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : format}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** 用户偏好与反馈 */
export interface UserProfile {
  holding_period_days?: number
  favorite_features?: Record<string, number>
  [k: string]: any
}

export async function getPreferences(): Promise<UserProfile> {
  const r = await api.get('/preferences')
  return r.data
}

export async function saveFeatures(body: {
  favorite_features: Record<string, number>
  holding_period_days?: number
}): Promise<UserProfile> {
  const r = await api.post('/preferences/features', body)
  return r.data
}

export async function getPromptPatch(): Promise<{ patch: string }> {
  const r = await api.get('/preferences/prompt-patch')
  return r.data
}

// ==================== Phase 3: 市场机会发现 ====================

/** 资金流: 大盘/板块/告警 */
export async function getMarketFundFlow(): Promise<any> {
  const r = await api.get('/fund-flow/market', { timeout: 60_000 }) // ALENS-175: 实测 25-27s 贴 30s 默认超时线
  return r.data
}

export async function getIndustryFundFlow(topN = 10): Promise<{
  top_inflow: Array<{ code: string; name: string; main_net: number; change_pct?: number }>
  top_outflow: Array<{ code: string; name: string; main_net: number; change_pct?: number }>
  data_available?: boolean
  message?: string
}> {
  const r = await api.get('/fund-flow/industries', { params: { top_n: topN } })
  return r.data
}

export async function getSymbolFundFlow(symbol: string): Promise<any> {
  const r = await api.get(`/fund-flow/symbol/${encodeURIComponent(symbol)}`)
  return r.data
}

export async function listFundFlowRules(): Promise<any[]> {
  const r = await api.get('/fund-flow/alerts/rules')
  return r.data.items || []
}

export async function addFundFlowRule(body: Record<string, any>): Promise<any> {
  const r = await api.post('/fund-flow/alerts/rules', body)
  return r.data
}

export async function deleteFundFlowRule(ruleId: string): Promise<void> {
  await api.delete(`/fund-flow/alerts/rules/${ruleId}`)
}

export async function listFundFlowTriggered(limit = 50): Promise<any[]> {
  const r = await api.get('/fund-flow/alerts/triggered', { params: { limit } })
  return r.data.items || []
}

/** AH 套利 */
export interface AhPair {
  pair_name: string
  a_symbol: string
  h_symbol: string
  premium_pct: number
  [k: string]: any
}

export async function getAhPairs(): Promise<AhPair[]> {
  const r = await api.get('/arbitrage/ah-pairs')
  return r.data.items || []
}

export async function scanAh(): Promise<any> {
  const r = await api.post('/arbitrage/scan-ah', {}, { timeout: 120_000 })
  return r.data
}

export async function listArbitrageSnapshots(limit = 20): Promise<any[]> {
  const r = await api.get('/arbitrage/snapshots', { params: { limit } })
  return r.data.items || []
}

/** 财报日历 */
export interface EarningsEvent {
  symbol: string
  name?: string
  market?: string
  report_date: string
  period?: string
  [k: string]: any
}

export async function listEarnings(opts: { days_ahead?: number; market?: string; limit?: number } = {}): Promise<{
  count: number
  items: EarningsEvent[]
  next_season_cn?: string
  next_season_hk?: string
}> {
  const r = await api.get('/calendar/earnings', { params: opts })
  return r.data
}









// ==================== Phase 4: 投研报告 ====================
export interface InvestmentReport {
  report_id: string
  report_type: string
  period_start?: string
  period_end?: string
  content_md?: string
  created_at?: string
  [k: string]: any
}

export async function generateReport(type: 'weekly' | 'monthly'): Promise<InvestmentReport> {
  const r = await api.post(`/reports/${type}`, {}, { timeout: 120_000 })
  return r.data
}

export async function listReports(limit = 20): Promise<InvestmentReport[]> {
  const r = await api.get('/reports/list', { params: { limit } })
  return r.data.items || []
}

export async function getReport(id: string): Promise<InvestmentReport> {
  const r = await api.get(`/reports/${id}`)
  return r.data
}

/** 整池分析 */
export async function analyzeWatchlist(
  watchlistId: string,
  body: { depth?: string } = {},
): Promise<any> {
  const r = await api.post(`/watchlist/${watchlistId}/analyze`, body, { timeout: 60_000 })
  return r.data
}

/** 审计日志 (admin) */
export async function listAuditLogs(
  params: { user?: string; user_id?: string; limit?: number; skip?: number; last_created_at?: string } = {}
): Promise<any[]> {
  const targetUser = params.user_id || params.user
  const r = await api.get('/audit/logs', {
    params: {
      limit: params.limit ?? 50,
      ...(params.skip != null ? { skip: params.skip } : {}),
      ...(params.last_created_at ? { last_created_at: params.last_created_at } : {}),
      ...(targetUser ? { user_id: targetUser, user: targetUser } : {})
    }
  })
  return r.data.items || []
}

// ==================== 尾批: 基金对比 / 因子库 / ML 预测 ====================
export interface FundCompareResultItem {
  symbol: string
  name: string
  category: string
  total_return: number
  annual_return: number
  volatility: number
  sharpe_ratio: number
  max_drawdown: number
  tracking_error: number
  information_ratio: number
  aum_billion: number
  expense_ratio: number
}

export interface FundCompareResponse {
  count: number
  items: FundCompareResultItem[]
  missing?: string[]
}

export async function compareFunds(symbols: string[], benchmark = 'sh.000300', periodDays = 252): Promise<FundCompareResponse> {
  const r = await api.post('/funds/compare', { symbols, benchmark, period_days: periodDays }, { timeout: 120_000 })
  return r.data
}

export interface FundScreenRequest {
  min_aum_billion?: number
  max_expense_ratio?: number
  min_sharpe_ratio?: number
  max_drawdown_limit?: number
  category?: string
}

export async function screenFunds(req: FundScreenRequest): Promise<any> {
  const r = await api.post('/funds/screen', req)
  return r.data
}

export async function compareStocks(symbols: string[]): Promise<any> {
  const r = await api.post('/stocks/compare', { symbols })
  return r.data
}

export async function scanAnomalies(payload: { symbols?: string[]; min_score?: number; auto_trigger?: boolean }): Promise<any> {
  const r = await api.post('/screener/anomalies/scan', payload)
  return r.data
}

export async function getKlineProjection(payload: { symbol: string; forecast_days?: number; temperature?: number }): Promise<any> {
  const r = await api.post('/analyze/kline-projection', payload)
  return r.data
}

export async function getRetrospectiveSummary(): Promise<any> {
  const r = await api.get('/analyze/retrospective/summary')
  return r.data
}

export async function getReflections(symbol: string): Promise<any> {
  const sym = symbol.trim() || 'all'
  const r = await api.get(`/analyze/reflections/${sym}`)
  return r.data
}

export async function getMacroIndicators(): Promise<any> {
  const r = await api.get('/macro/indicators')
  return r.data
}

export async function getMacroAssetAllocation(): Promise<any> {
  const r = await api.get('/macro/asset-allocation')
  return r.data
}

export async function getFactorLibrary(): Promise<any[]> {
  const r = await api.get('/factors/library')
  return r.data.items || []
}

export async function mlPredict(body: { symbols: string[]; forward_days?: number; top_k?: number }): Promise<any> {
  const r = await api.post('/factors/ml-predict', body, { timeout: 120_000 })
  return r.data
}

// ==================== QuantDB 机构级多因子与历史回测 ====================
export interface QuantDBFactorMeta {
  id: string
  name: string
  table: string
  column: string
  category: string
  direction: number
  description: string
}

export interface QuantDBCategoryItem {
  id: string
  name: string
  count: number
}

export interface QuantDBCatalogResponse {
  total: number
  count?: number
  categories: Array<string | QuantDBCategoryItem>
  items: QuantDBFactorMeta[]
}

export interface QuantDBEvaluateRequest {
  factor_id: string
  start_date?: string
  end_date?: string
  forward_days?: number
  quantiles?: number
  symbols?: string[]
  cost_rate?: number
  max_days?: number
}

export interface QuantDBBacktestMetrics {
  total_return: number
  annualized_return: number
  annual_volatility: number
  sharpe_ratio: number
  max_drawdown: number
  win_rate: number
  profit_loss_ratio: number
  calmar_ratio: number
  monotonicity_score?: number
}

export interface QuantDBFactorEvalResult {
  factor_id: string
  factor_name: string
  category: string
  table: string
  direction: number
  start_date: string
  end_date: string
  periods_count: number
  sample_stocks_mean: number
  ic_mean: number
  ic_std: number
  icir: number
  rank_ic_mean: number
  rank_ic_std: number
  rank_icir: number
  ic_positive_pct: number
  t_stat: number
  ic_decay: Record<string, number>
  ic_series: Array<{
    date: string
    ic: number
    rank_ic: number
    cumulative_rank_ic: number
  }>
  quantile_returns: Record<string, number>
  quantile_curves: Array<Record<string, any>>
  quantiles?: number
  backtest_metrics: QuantDBBacktestMetrics
  warnings: string[]
}

export interface QuantDBBacktestRequest {
  factor_id: string
  start_date?: string
  end_date?: string
  forward_days?: number
  quantiles?: number
  initial_capital?: number
  cost_rate?: number
  max_days?: number
}

export interface QuantDBBacktestResult {
  factor_id: string
  factor_name: string
  initial_capital: number
  backtest_metrics: QuantDBBacktestMetrics
  quantile_returns: Record<string, number>
  capital_curves: Array<Record<string, any>>
  equity_curves: Array<Record<string, any>>
  periods_count: number
  start_date: string
  end_date: string
  warnings: string[]
}

export async function getQuantDBCatalog(category?: string): Promise<QuantDBCatalogResponse> {
  const r = await api.get('/factors/quantdb/catalog', { params: category ? { category } : {} })
  return r.data
}

export async function evaluateQuantDBFactor(body: QuantDBEvaluateRequest): Promise<QuantDBFactorEvalResult> {
  const r = await api.post('/factors/quantdb/evaluate', body, { timeout: 120_000 })
  return r.data
}

export async function backtestQuantDBFactor(body: QuantDBBacktestRequest): Promise<QuantDBBacktestResult> {
  const r = await api.post('/factors/quantdb/backtest', body, { timeout: 120_000 })
  return r.data
}

export async function getStockQuantDBHistory(
  symbol: string,
  factors?: string,
  days?: number
): Promise<{ symbol: string; factors: string[]; count: number; history: Array<Record<string, any>> }> {
  const r = await api.get('/factors/quantdb/stock-history', {
    params: {
      symbol,
      ...(factors ? { factors } : {}),
      ...(days ? { days } : {})
    }
  })
  return r.data
}

// ===== 生产因子引擎 (ALENS-57): TickerLab 实时预计算, QuantDB 仅作参照 =====

export interface ProductionCatalogResponse {
  count: number
  source: string
  latest_date: string | null
  categories: Array<{ id: string; name: string; count: number }>
  items: QuantDBFactorMeta[]
  note?: string
}

export interface ProductionLatestResponse {
  factor_id: string
  factor_name: string
  direction: string
  date: string
  count: number
  top: Array<{ symbol: string; value: number }>
  bottom: Array<{ symbol: string; value: number }>
}

export interface ProductionValidateResponse {
  dates_used: string[]
  factors: Array<{ factor_id: string; dates_compared: number; mean_corr: number | null; pass: boolean }>
  factors_total: number
  factors_compared: number
  factors_passed: number
  corr_threshold: number
  note: string
}

export async function getProductionCatalog(): Promise<ProductionCatalogResponse> {
  const r = await api.get('/factors/production/catalog')
  return r.data
}

export async function evaluateProductionFactor(
  body: QuantDBEvaluateRequest
): Promise<QuantDBFactorEvalResult> {
  const r = await api.post('/factors/production/evaluate', body, { timeout: 120_000 })
  return r.data
}

export async function backtestProductionFactor(
  body: QuantDBBacktestRequest
): Promise<QuantDBBacktestResult> {
  const r = await api.post('/factors/production/backtest', body, { timeout: 120_000 })
  return r.data
}

export async function getStockProductionHistory(
  symbol: string,
  factors?: string,
  days?: number
): Promise<{ symbol: string; factors: string[]; count: number; history: Array<Record<string, any>> }> {
  const r = await api.get('/factors/production/stock-history', {
    params: {
      symbol,
      ...(factors ? { factors } : {}),
      ...(days ? { days } : {})
    }
  })
  return r.data
}

export async function getProductionLatest(
  factorId: string,
  date?: string,
  top?: number
): Promise<ProductionLatestResponse> {
  const r = await api.get('/factors/production/latest', {
    params: { factor_id: factorId, ...(date ? { date } : {}), ...(top ? { top } : {}) }
  })
  return r.data
}

export async function validateProductionFactors(body: {
  factor_ids?: string[]
  max_days?: number
  min_overlap?: number
  corr_threshold?: number
}): Promise<ProductionValidateResponse> {
  const r = await api.post('/factors/production/validate', body, { timeout: 120_000 })
  return r.data
}

export interface MultiFactorWeightItem {
  factor_id: string
  weight: number
  direction?: number
}

export interface MultiFactorSynthesizeRequest {
  date?: string
  factors: MultiFactorWeightItem[]
  method?: 'custom' | 'equal' | 'ic_weight'
  top_n?: number
}

export interface MultiFactorEvaluateRequest {
  factors: MultiFactorWeightItem[]
  method?: 'custom' | 'equal' | 'ic_weight'
  start_date?: string
  end_date?: string
  forward_days?: number
  quantiles?: number
  symbols?: string[]
  cost_rate?: number
  max_days?: number
}

export interface MultiFactorSynthesizeResponse {
  date: string
  total_stocks: number
  method: string
  factors: MultiFactorWeightItem[]
  top_stocks: Array<{
    rank: number
    symbol: string
    composite_score: number
    components: Record<string, number | null>
  }>
  bottom_stocks: Array<{
    rank: number
    symbol: string
    composite_score: number
    components: Record<string, number | null>
  }>
}

export async function synthesizeProductionFactors(
  body: MultiFactorSynthesizeRequest
): Promise<MultiFactorSynthesizeResponse> {
  const r = await api.post('/factors/production/synthesize', body)
  return r.data
}

export async function evaluateSynthesizedProductionFactors(
  body: MultiFactorEvaluateRequest
): Promise<QuantDBFactorEvalResult> {
  const r = await api.post('/factors/production/synthesize/evaluate', body, { timeout: 120_000 })
  return r.data
}

export async function synthesizeQuantDBFactors(
  body: MultiFactorSynthesizeRequest
): Promise<MultiFactorSynthesizeResponse> {
  const r = await api.post('/factors/quantdb/synthesize', body)
  return r.data
}

// =========================================================================
// 因子回测平台 (ALENS-66)
// =========================================================================

export interface FactorBacktestMeta {
  data_ready: boolean
  latest_kline_date: string | null
  max_days: number
  benchmarks: Array<{ symbol: string; name: string }>
  rebalance_modes: string[]
  weight_schemes: string[]
  signal_kinds: string[]
  defaults: Record<string, number | string>
}

export interface FactorBacktestSignalBody {
  kind: 'quantdb_factor' | 'multi_factor' | 'signal_panel' | 'expression'
  factor_id?: string
  factors?: Array<{ factor_id: string; weight?: number; direction?: number }>
  method?: string
  panel_id?: string
  expression?: string
  direction?: number
  min_scores?: number
}

export interface FactorBacktestRunRequest {
  signal: FactorBacktestSignalBody
  start_date?: string
  end_date?: string
  max_days?: number
  initial_capital?: number
  top_n?: number
  weight_scheme?: string
  rebalance?: string
  max_weight?: number
  min_amount?: number
  benchmark?: string
  commission_rate?: number
  commission_min?: number
  stamp_duty_rate?: number
  slippage_bps?: number
}

export interface FactorBacktestTrade {
  date: string
  symbol: string
  side: 'buy' | 'sell'
  price: number
  shares: number
  value: number
  commission: number
  stamp_duty: number
}

export interface FactorBacktestDailyRecord {
  date: string
  cash: number
  position_value: number
  equity: number
  n_positions: number
  benchmark: number | null
}

export interface FactorBacktestResult {
  config: Record<string, unknown>
  signal: Record<string, unknown>
  data_range: { start: string; end: string; n_days: number; n_symbols: number }
  daily_records: FactorBacktestDailyRecord[]
  trades: FactorBacktestTrade[]
  rebalance_events: Array<Record<string, unknown>>
  metrics: Record<string, number | null>
  drawdown: Array<{ date: string; drawdown: number }>
  monthly_returns: Record<string, Array<number | null>>
  yearly_returns: Record<string, number>
  warnings: string[]
}

export async function fetchFactorBacktestMeta(): Promise<FactorBacktestMeta> {
  const r = await api.get('/factor-backtest/meta')
  return r.data
}

export async function runFactorBacktest(
  body: FactorBacktestRunRequest
): Promise<FactorBacktestResult> {
  const r = await api.post('/factor-backtest/run', body, { timeout: 300_000 })
  return r.data
}

export interface LlmSignalPanelSummary {
  panel_id: string
  n_records: number
  n_dates: number
  n_symbols: number
  date_start: string
  date_end: string
  source: string
  model: string
  created_at: string | null
}

export async function listLlmSignalPanels(): Promise<{ panels: LlmSignalPanelSummary[] }> {
  const r = await api.get('/llm-signals/panels')
  return r.data
}

export async function generateLlmSignalPanel(body: {
  symbols: string[]
  dates: string[]
  panel_id?: string
}): Promise<Record<string, unknown>> {
  const r = await api.post('/llm-signals/generate', body, { timeout: 300_000 })
  return r.data
}

export async function importLlmSignalPanel(body: {
  panel_id: string
  source?: string
  records: Array<{ date: string; symbol: string; score: number; rationale?: string }>
}): Promise<Record<string, unknown>> {
  const r = await api.post('/llm-signals/import', body, { timeout: 60_000 })
  return r.data
}



// =========================================================================
// 线上回测: 策略 CRUD + 运行记录 + 表达式引擎 (ALENS-67)
// =========================================================================

export interface ExpressionMeta {
  variables: Array<{ name: string; desc: string }>
  functions: string[]
  examples: string[]
}

export interface ExpressionValidateResult {
  valid: boolean
  error: string | null
  expression?: string
  variables?: string[]
  functions?: string[]
}

export interface ExpressionEvalResult {
  factor_id: string
  factor_name: string
  periods_count: number
  ic_mean: number
  ic_std: number
  icir: number
  rank_ic_mean: number
  t_stat: number
  ic_positive_pct: number
  quantile_returns: Record<string, number>
  ic_series: Array<{ date: string; ic: number; rank_ic: number }>
  warnings: string[]
}

export async function fetchExpressionMeta(): Promise<ExpressionMeta> {
  const r = await api.get('/factors/expression/variables')
  return r.data
}

export async function validateFactorExpression(
  expression: string,
  forSignal = true
): Promise<ExpressionValidateResult> {
  const r = await api.post('/factors/expression/validate', { expression, for_signal: forSignal })
  return r.data
}

export async function evaluateFactorExpression(body: {
  expression: string
  forward_days?: number
  quantiles?: number
  max_days?: number
  symbols?: string[]
}): Promise<ExpressionEvalResult> {
  const r = await api.post('/factors/expression/evaluate', body, { timeout: 180_000 })
  return r.data
}

export interface BacktestStrategy {
  strategy_id: string
  user?: string
  name: string
  description: string
  signal: FactorBacktestSignalBody
  config: Record<string, unknown>
  schedule_freq: 'none' | 'daily' | 'weekly'
  schedule_hour: number
  schedule_weekday: number
  enabled: boolean
  created_at?: string
  updated_at?: string
  last_run_at?: string
}

export interface BacktestRunSummary {
  run_id: string
  strategy_id: string
  strategy_name: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  trigger: 'manual' | 'scheduled' | 'api'
  progress: number
  stage: string
  error: string | null
  metrics: Record<string, number | null> | null
  data_range: { start: string; end: string; n_days: number; n_symbols: number } | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
}

export async function listBacktestStrategies(): Promise<BacktestStrategy[]> {
  const r = await api.get('/factor-backtest/strategies')
  return r.data.items || []
}

export async function createBacktestStrategy(body: {
  name: string
  signal: FactorBacktestSignalBody
  config?: Record<string, unknown>
  description?: string
  schedule_freq?: string
  schedule_hour?: number
  schedule_weekday?: number
  enabled?: boolean
}): Promise<BacktestStrategy> {
  const r = await api.post('/factor-backtest/strategies', body)
  return r.data
}

export interface BacktestStrategyUpdate {
  name?: string
  description?: string
  signal?: FactorBacktestSignalBody
  config?: Record<string, unknown>
  schedule_freq?: 'none' | 'daily' | 'weekly'
  schedule_hour?: number
  schedule_weekday?: number
  enabled?: boolean
}

export async function updateBacktestStrategy(
  strategyId: string,
  body: BacktestStrategyUpdate
): Promise<BacktestStrategy> {
  const r = await api.patch(`/factor-backtest/strategies/${strategyId}`, body)
  return r.data
}

export async function deleteBacktestStrategy(strategyId: string): Promise<void> {
  await api.delete(`/factor-backtest/strategies/${strategyId}`)
}

export async function triggerBacktestStrategyRun(
  strategyId: string,
  maxDays = 250
): Promise<BacktestRunSummary> {
  const r = await api.post(
    `/factor-backtest/strategies/${strategyId}/run`,
    { max_days: maxDays },
    { timeout: 60_000 }
  )
  return r.data
}

export async function listBacktestRuns(
  strategyId = '',
  limit = 50
): Promise<BacktestRunSummary[]> {
  const r = await api.get('/factor-backtest/runs', {
    params: { ...(strategyId ? { strategy_id: strategyId } : {}), limit },
  })
  return r.data.items || []
}

export async function getBacktestRun(runId: string): Promise<BacktestRunSummary> {
  const r = await api.get(`/factor-backtest/runs/${runId}`)
  return r.data
}

/** SSE: 回测运行实时进度 (EventSource 无法带 header, 走 access_token query) */
export function openBacktestRunStream(
  runId: string,
  onProgress: (evt: { node: string; status: string; detail: string; progress: number }) => void,
  onDone?: () => void,
  onReplayDone?: () => void,
): StreamHandle {
  return openTicketStream(
    (ticket) =>
      `${baseURL}/factor-backtest/runs/${runId}/stream?ticket=${encodeURIComponent(ticket)}`,
    (es) => {
      es.addEventListener('progress', (ev) => {
        try {
          onProgress(JSON.parse(ev.data))
        } catch (e) {
          console.error('SSE parse error', e)
        }
      })
      if (onReplayDone) {
        es.addEventListener('replay_done', () => onReplayDone())
      }
    },
    { onDrop: onDone },
  )
}

/* ==========================================================================
 * 回测案例库 (ALENS-68)
 * ========================================================================== */

export interface BacktestCaseExpectations {
  min_trades: number
  metrics_at_least: Record<string, number>
  max_drawdown_limit: number
  min_curve_points: number
}

export interface BacktestCaseSpec {
  case_id: string
  name: string
  category: string
  summary: string
  source: string
  default_params: Record<string, unknown>
  expectations: BacktestCaseExpectations
  n_symbols: number
}

export interface QuantDBStatus {
  available: boolean
  latest_date: string | null
  reason?: string
}

export interface BacktestCaseCatalog {
  items: BacktestCaseSpec[]
  by_category: Record<string, string[]>
  limits: { min_bars: number; max_bars: number; default_bars: number }
  engine: string
  engine_ready?: boolean
  quantdb?: QuantDBStatus
}

export async function getBacktestCaseCatalog(): Promise<BacktestCaseCatalog> {
  const r = await api.get('/backtest-cases')
  return r.data
}

/** 生产行情可用性快照 (ALENS-84): 前端「真行情 · 截至 X」横幅 */
export async function getBacktestDataStatus(): Promise<QuantDBStatus> {
  const r = await api.get('/backtest-cases/data-status')
  return r.data
}

export async function runBacktestCase(
  caseId: string,
  payload: {
    params?: Record<string, unknown>
    n_bars?: number
    initial_capital?: number
    progress_every?: number
    prefer_synthetic?: boolean
  }
): Promise<{ run_id: string; status: string; stream_url: string }> {
  const r = await api.post(`/backtest-cases/${caseId}/run`, payload)
  return r.data
}

export async function getBacktestCaseRun(runId: string): Promise<Record<string, any>> {
  const r = await api.get(`/backtest-cases/runs/${runId}`)
  return r.data
}

/** 运行历史摘要 (ALENS-78 / G4): 列表端点不带 equity_curve/trades 大字段 */
export interface BacktestCaseRunSummary {
  run_id: string
  case_id: string
  case_name?: string
  category?: string
  status: string
  n_bars?: number
  initial_capital?: number
  metrics?: Record<string, number>
  trade_count?: number
  equity_points?: number
  data_source?: string
  data_latest_date?: string
  created_at?: string
  completed_at?: string
  error?: string
}

export async function listBacktestCaseRuns(params?: {
  case_id?: string
  limit?: number
}): Promise<BacktestCaseRunSummary[]> {
  const r = await api.get('/backtest-cases/runs', { params })
  return r.data.items || []
}

/** SSE: 案例回测实时进度 */
export function openBacktestCaseStream(
  runId: string,
  onEvent: (evt: Record<string, any>) => void,
  onDone?: () => void
): StreamHandle {
  return openTicketStream(
    (ticket) => `${baseURL}/backtest-cases/runs/${runId}/stream?ticket=${encodeURIComponent(ticket)}`,
    (es) => {
      es.addEventListener('progress', (ev) => {
        try {
          onEvent(JSON.parse(ev.data))
        } catch (e) {
          console.error('SSE parse error', e)
        }
      })
    },
    { onDrop: onDone },
  )
}

/* ==========================================================================
 * 策略实验室 (ALENS-68, 需求 2)
 * ========================================================================== */

export interface StrategyValidation {
  passed: boolean
  errors: string[]
  warnings: string[]
  classes: string[]
  functions: string[]
}

export interface StrategyExample {
  name: string
  factors: Record<string, string>
  code: string
}

export async function getStrategyTemplate(): Promise<{
  code: string
  name: string
  examples?: StrategyExample[]
}> {
  const r = await api.get('/strategy-lab/template')
  return r.data
}

export async function validateStrategyCode(code: string): Promise<StrategyValidation> {
  const r = await api.post('/strategy-lab/validate', { code })
  return r.data
}

export interface UserStrategySummary {
  strategy_id: string
  name: string
  description: string
  detected_classes: string[]
  created_at: string
  updated_at: string
}

export async function listUserStrategies(): Promise<UserStrategySummary[]> {
  const r = await api.get('/strategy-lab/strategies')
  return r.data.items || []
}

export async function getUserStrategy(strategyId: string): Promise<any> {
  const r = await api.get(`/strategy-lab/strategies/${strategyId}`)
  return r.data
}

export async function createUserStrategy(payload: {
  name: string
  code: string
  description?: string
}): Promise<any> {
  const r = await api.post('/strategy-lab/strategies', payload)
  return r.data
}

export async function updateUserStrategy(
  strategyId: string,
  payload: { name?: string; code?: string; description?: string }
): Promise<any> {
  const r = await api.patch(`/strategy-lab/strategies/${strategyId}`, payload)
  return r.data
}

export async function deleteUserStrategy(strategyId: string): Promise<void> {
  await api.delete(`/strategy-lab/strategies/${strategyId}`)
}

export interface StrategyRunRequest {
  strategy_id?: string
  code?: string
  symbol?: string
  /** 多标的组合 (ALENS-85): ≤8, 空则退回单 symbol */
  symbols?: string[]
  n_bars?: number
  initial_capital?: number
  commission_rate?: number
  step_budget?: number
  timeout_s?: number
  class_name?: string
  prefer_synthetic?: boolean
  /** ALENS-94: 因子定义 {名: 表达式} (AST 白名单, ≤4) */
  factors?: Record<string, string>
}

export async function runUserStrategy(
  payload: StrategyRunRequest
): Promise<{ run_id: string; status: string; stream_url: string; data_source: string }> {
  const r = await api.post('/strategy-lab/run', payload)
  return r.data
}

export async function getUserStrategyRun(runId: string): Promise<Record<string, any>> {
  const r = await api.get(`/strategy-lab/runs/${runId}`)
  return r.data
}

/** 运行历史摘要 (ALENS-78 / G4): 列表端点不带 equity_curve/trades 大字段 */
export interface UserStrategyRunSummary {
  run_id: string
  strategy_id?: string | null
  strategy_class?: string
  symbol?: string
  status: string
  n_bars?: number
  metrics?: Record<string, number>
  trade_count?: number
  bars?: number
  final_equity?: number
  data_source?: string
  created_at?: string
  completed_at?: string
  error?: string
}

export async function listUserStrategyRuns(params?: {
  strategy_id?: string
  limit?: number
}): Promise<UserStrategyRunSummary[]> {
  const r = await api.get('/strategy-lab/runs', { params })
  return r.data.items || []
}

/** SSE: 用户策略执行过程 (校验 / 日志 / 成交 / 进度 / 结果) */
export function openUserStrategyStream(
  runId: string,
  onEvent: (evt: Record<string, any>) => void,
  onDone?: () => void
): StreamHandle {
  return openTicketStream(
    (ticket) => `${baseURL}/strategy-lab/runs/${runId}/stream?ticket=${encodeURIComponent(ticket)}`,
    (es) => {
      es.addEventListener('progress', (ev) => {
        try {
          onEvent(JSON.parse(ev.data))
        } catch (e) {
          console.error('SSE parse error', e)
        }
      })
    },
    { onDrop: onDone },
  )
}

/* ==========================================================================
 * 模拟盘部署 (ALENS-72)
 *
 * 部署 = 案例库的一个 case_id + params, 用**与回测同一份**引擎周期推进。
 * `tick` 会真的拉起一次引擎子进程, 因此超时单独放宽 (默认 30s 不够用)。
 *
 * 错误语义: 409/422 的 `detail` 是要**原样展示给用户**的中文原因
 * (如 live_mode 未接通), 所以这里不吞错误 —— 响应拦截器已把 `detail`
 * 写进 `error.message`, 视图层用 extractErrorMessage 取原文。
 * ========================================================================== */

/** 部署状态。`stopped` 是终态, 只能人工重新拉起。 */
export type PaperDeploymentStatus = 'running' | 'paused' | 'stopped'

/** 一次 tick 产出的订单动作 (后端 DeploymentAction 的对外投影)。 */
export interface PaperDeploymentAction {
  symbol: string
  /** "open_long" | "close" */
  action: string
  /** 中文原因串, 如「策略目标净持仓 41 > 0, 当前无仓」 */
  reason: string
}

export interface PaperDeploymentEquityPoint {
  date: string
  equity: number
  cash?: number
  market_value?: number
}

export interface PaperDeployment {
  deployment_id: string
  user_id: string
  name: string
  /** 来源类型: "case_library" | "strategy_lab" */
  source: string
  case_id?: string | null
  strategy_id?: string | null
  strategy_code?: string | null
  params: Record<string, unknown>
  symbols: string[]
  lookback_bars: number
  initial_capital: number
  source_run_id: string | null
  equity_history?: PaperDeploymentEquityPoint[]
  /** 实盘预留开关: 为真时推进会**显式失败** (实盘通道尚未接通) */
  live_mode: boolean
  status: PaperDeploymentStatus
  run_count: number
  last_run_at: string | null
  last_error: string | null
  created_at: string
  updated_at: string
}

export interface PaperDeploymentTickResult {
  deployment_id: string
  actions?: PaperDeploymentAction[]
  orders?: unknown[]
  /** 本次没有真正推进 (状态非 running / 引擎不可用), reason 是真实原因 */
  skipped?: boolean
  reason?: string
}

export interface PaperDeploymentComparison {
  deployment_id: string
  name: string
  source: string
  has_backtest_run: boolean
  source_run_id?: string | null
  metrics: {
    paper_return: number
    backtest_return: number
    return_divergence: number
    paper_max_drawdown: number
    backtest_max_drawdown: number
    drawdown_divergence: number
    is_divergent: boolean
    divergence_warning?: string | null
  }
  data_sources: {
    paper_source: string
    backtest_source: string
    is_cross_source: boolean
    source_warning?: string | null
  }
  comparison_series: {
    paper: { date?: string; equity: number; raw_equity?: number }[]
    backtest: { date?: string; equity: number; raw_equity?: number }[]
  }
}

export async function getPaperDeployments(): Promise<PaperDeployment[]> {
  const r = await api.get('/paper-deployments')
  return r.data.items || []
}

export async function createPaperDeployment(body: {
  case_id?: string
  code?: string
  strategy_code?: string
  source?: 'case_library' | 'strategy_lab'
  strategy_id?: string
  name?: string
  params?: Record<string, unknown>
  symbols?: string[]
  lookback_bars?: number
  initial_capital?: number
  source_run_id?: string
  live_mode?: boolean
  /** ALENS-94: 因子定义 (随部署持久化, 每 tick 重新求值) */
  factors?: Record<string, string>
}): Promise<PaperDeployment> {
  const r = await api.post('/paper-deployments', body)
  return r.data
}

export async function setPaperDeploymentStatus(
  deploymentId: string,
  status: PaperDeploymentStatus
): Promise<PaperDeployment> {
  const r = await api.post(`/paper-deployments/${deploymentId}/status`, { status })
  return r.data
}

/** 手动推进一次。会拉起引擎子进程跑滚动窗口, 很慢 —— 超时放宽到 5 分钟。 */
export async function tickPaperDeployment(deploymentId: string): Promise<PaperDeploymentTickResult> {
  const r = await api.post(`/paper-deployments/${deploymentId}/tick`, {}, { timeout: 300_000 })
  return r.data
}

export async function deletePaperDeployment(deploymentId: string): Promise<void> {
  await api.delete(`/paper-deployments/${deploymentId}`)
}

export async function getPaperDeploymentComparison(
  deploymentId: string
): Promise<PaperDeploymentComparison> {
  const r = await api.get(`/paper-deployments/${deploymentId}/compare`)
  return r.data
}

// ------------------------------------------------------------- 单股决策一键转模拟挂单 (ALENS-118)

export interface PaperOrderResponse {
  id?: string
  order_id?: string
  user_id: string
  analysis_id: string
  symbol: string
  action: 'BUY' | 'SELL'
  price: number
  target_shares?: number
  quantity?: number
  status: string
  created_at: string
}

export async function createPaperOrderFromDecision(
  analysisId: string
): Promise<PaperOrderResponse> {
  const r = await api.post(`/paper/orders/${analysisId}`)
  return r.data
}

// ------------------------------------------------------------- 网格寻优 (ALENS-89)

export interface OptimizeRequest {
  strategy_id?: string
  code?: string
  symbol?: string
  symbols?: string[]
  /** {参数名: [离散取值]} —— 用户代码经 self.param() 读取 */
  params_grid: Record<string, (string | number)[]>
  n_bars?: number
  split_ratio?: number
  /** ALENS-96: single 保持 ALENS-89 单次切分; rolling/anchored 为 walk-forward */
  wf_mode?: 'single' | 'rolling' | 'anchored'
  wf_windows?: number
  /** ALENS-94: 因子定义 {名: 表达式} (AST 白名单, ≤4) */
  factors?: Record<string, string>
  initial_capital?: number
  commission_rate?: number
  step_budget?: number
  prefer_synthetic?: boolean
}

export interface OptimizationRankingRow {
  rank: number
  params: Record<string, unknown>
  train: Record<string, number>
  valid: Record<string, number>
  train_error?: string | null
  valid_error?: string | null
  robust: boolean
  valid_checked: boolean
}

export interface WfSummaryRow {
  params: Record<string, unknown>
  topk_windows: number
  robust_windows: number
  checked_windows: number
  valid_win_rate?: number | null
  valid_sharpe_median?: number | null
  valid_sharpe_min?: number | null
  wf_robust: boolean
}

export interface OptimizationDetail {
  opt_id: string
  status: 'running' | 'completed' | 'failed'
  combo_count: number
  /** ALENS-96: walk-forward 模式与跨窗口汇总 (单次切分时缺省) */
  mode?: 'rolling' | 'anchored'
  wf_summary?: {
    rows: WfSummaryRow[]
    window_count: number
    judgement: string
  }
  windows?: unknown[]
  params_grid?: Record<string, (string | number)[]>
  ranking?: OptimizationRankingRow[]
  best?: OptimizationRankingRow | null
  robust_count?: number
  split?: {
    ratio: number
    train_bars: number
    valid_bars: number
    train_end?: string | null
    valid_start?: string | null
    valid_end?: string | null
  }
  data_source?: string
  data_latest_date?: string | null
  /** ALENS-95: 数据版本锚 (真行情寻优才有版本戳) */
  data_anchor?: { version?: string | null; latest_date?: string | null } | null
  error?: string
  error_detail?: string
}

export async function startOptimize(payload: OptimizeRequest): Promise<{
  opt_id: string
  status: string
  combo_count: number
}> {
  const r = await api.post('/strategy-lab/optimize', payload)
  return r.data
}

export async function getOptimization(optId: string): Promise<OptimizationDetail> {
  const r = await api.get(`/strategy-lab/optimizations/${optId}`)
  return r.data
}

// ==================== Admin: Agent Backtest (ALENS-193) ====================
// 管理员决策回测网格工作台: 消费 /api/admin/agent-backtest 既有五端点。
// 非管理员: 后端 403 (get_current_admin_user), 前端入口隐藏 (requiresAdmin)。

export interface SweepRunSettings {
  quick_llm?: string | null
  deep_llm?: string | null
  debate_max_rounds?: number
  depth?: string
  trade_date?: string | null
  graph_version?: string
  data_sources?: string[]
}

export interface SweepCellsCounter {
  total: number
  completed: number
  failed: number
  settled: number
  unsettled: number
}

export interface SweepRatingStats {
  count: number
  hits: number | null
  hit_rate: number | null
  tracking_band_rate: number | null
  avg_alpha: number
}

export interface SweepTraderExecution {
  total_directional: number
  target_hits: number
  target_hit_rate: number | null
  stopped_outs: number
  stop_out_rate: number | null
  profit_loss_ratio: number | null
}

export interface SweepSummary {
  metric_version: string
  sweep_id: string
  generated_at: string
  cells: SweepCellsCounter
  by_rating: Record<string, SweepRatingStats>
  overall: {
    directional_cells: number
    direction_hit_rate: number | null
    avg_alpha: number | null
    avg_directional_alpha: number | null
  }
  trader_execution?: SweepTraderExecution
  hold_baseline: { tracking_band_rate: number | null; avg_alpha: number | null; definition: string }
  run_settings?: SweepRunSettings
  disclosure?: string
}

export interface HistoricalRegime {
  id: string
  name: string
  description: string
  dates: string[]
  symbols: string[]
  benchmark: string
}

export interface CalibrationParams {
  min_confidence?: number
  strict_munger_veto?: boolean
}

export interface CalibrationResult {
  params: { min_confidence: number; strict_munger_veto: boolean }
  baseline: { directional_cells: number; hit_rate: number | null; avg_alpha: number | null }
  calibrated: { directional_cells: number; hit_rate: number | null; avg_alpha: number | null }
  delta: { hit_rate_delta: number; alpha_delta: number; prevented_losses: number; missed_gains: number }
}

export interface SweepListItem {
  sweep_id: string
  status: string
  depth: string
  created_at: string
  completed_at?: string | null
  symbols?: string[]
  dates?: string[]
  counters?: { completed?: number; failed?: number } & Record<string, number>
  summary?: SweepSummary | null
}

export interface SweepCell {
  analysis_id: string
  symbol: string
  trade_date: string
  status: string
  rating?: string | null
  rating_direction?: number | null
  settled?: boolean
  metric_version?: string
  evaluation_status?: string
  pending_reason?: string | null
  error?: string | null
  settlement?: { resolved?: boolean; alpha_return?: number | null } | null
}

export const SWEEP_MAX_SYMBOLS = 20
export const SWEEP_MAX_DATES = 30

export async function createAgentBacktestSweep(body: {
  symbols: string[]
  dates: string[]
  depth?: AnalysisDepth
}): Promise<{ sweep_id: string; cells: number }> {
  const r = await api.post('/admin/agent-backtest/sweeps', body)
  return r.data
}

export async function listAgentBacktestSweeps(
  limit = 20,
): Promise<{ count: number; sweeps: SweepListItem[] }> {
  const r = await api.get('/admin/agent-backtest/sweeps', { params: { limit } })
  return r.data
}

export async function getAgentBacktestSweep(sweepId: string): Promise<SweepListItem> {
  const r = await api.get(`/admin/agent-backtest/sweeps/${sweepId}`)
  return r.data
}

export async function summarizeAgentBacktestSweep(sweepId: string): Promise<SweepSummary> {
  const r = await api.post(`/admin/agent-backtest/sweeps/${sweepId}/summarize`)
  return r.data
}

export async function getAgentBacktestSweepCells(
  sweepId: string,
): Promise<{ cells: SweepCell[]; count: number }> {
  const r = await api.get(`/admin/agent-backtest/sweeps/${sweepId}/cells`)
  return r.data
}

export async function getAgentBacktestRegimes(): Promise<{ regimes: HistoricalRegime[] }> {
  const r = await api.get<{ regimes: HistoricalRegime[] }>('/admin/agent-backtest/regimes')
  return r.data
}

export async function calibrateAgentBacktestSweep(
  sweepId: string,
  params: CalibrationParams,
): Promise<CalibrationResult> {
  const r = await api.post<CalibrationResult>(`/admin/agent-backtest/sweeps/${sweepId}/calibrate`, params)
  return r.data
}

// ---------------------------------------------------------------------------
// W6: durable research runs, three-arm evaluation, paper execution, automation.
// Mirrors apps/api/routes/{evaluation,execution,automation}.py. The server owns
// identity, approval, risk and ledger facts; nothing here grants authority.
// ---------------------------------------------------------------------------

export interface ListPage<T> {
  items: T[]
  limit: number
  offset: number
}

export interface ResearchRunVersions {
  model_version?: string
  graph_version?: string
  prompt_versions?: Record<string, string>
  node_versions?: Record<string, string>
  [key: string]: unknown
}

export interface ResearchLlmIo {
  research_id: string
  attempt_id: string
  payload_sha256: string
  created_at: string
  [key: string]: unknown
}

export interface ResearchRun {
  research_id: string
  owner_id: string
  namespace: string
  symbol: string
  name: string
  status: string
  trigger: string
  created_at: string
  started_at?: string | null
  heartbeat_at?: string | null
  lease_expires_at?: string | null
  completed_at?: string | null
  finished_at?: string | null
  attempt_id?: string
  dedup_key?: string
  discovery_id?: string
  as_of?: string
  pool_version?: string
  llm_token_budget?: number
  input_refs?: Record<string, unknown>
  versions?: ResearchRunVersions
  last_error?: string
  llm_io?: ResearchLlmIo[]
  [key: string]: unknown
}

export async function listResearchRuns(
  limit = 20,
  offset = 0,
): Promise<ListPage<ResearchRun>> {
  const r = await api.get<ListPage<ResearchRun>>('/eval/research-runs', {
    params: { limit, offset },
  })
  return r.data
}

export async function getResearchRun(researchId: string): Promise<ResearchRun> {
  const r = await api.get<ResearchRun>(`/eval/research-runs/${researchId}`)
  return r.data
}

export async function cancelResearchRun(researchId: string): Promise<ResearchRun> {
  const r = await api.post<ResearchRun>(`/eval/research-runs/${researchId}/cancel`)
  return r.data
}

export interface DiscoveryItem {
  symbol: string
  dedup_key: string
  status: string
  research_id?: string | null
  error?: string
  [key: string]: unknown
}

export interface DiscoveryRun {
  discovery_id: string
  owner_id: string
  namespace: string
  trigger: string
  priority: number
  status: string
  created_at: string
  started_at?: string | null
  finished_at?: string | null
  deadline?: string
  last_error?: string
  paused_scope?: string
  paused_reason?: string
  dispatched?: number
  skipped?: string[]
  pool?: { as_of?: string; pool_version?: string; symbols?: string[] }
  budget?: {
    max_runs?: number
    max_llm_tokens?: number
    per_run_llm_tokens?: number
    reserved_llm_tokens?: number
  }
  items?: DiscoveryItem[]
  [key: string]: unknown
}

export interface DiscoveryCreateRequest {
  trigger: string
  symbols?: string[]
  priority?: number
  max_runs?: number
  max_llm_tokens?: number
  per_run_llm_tokens?: number
  deadline_seconds?: number
  dedup_window_hours?: number
  pool_label?: string
}

export async function listDiscoveryRuns(
  limit = 20,
  offset = 0,
): Promise<ListPage<DiscoveryRun>> {
  const r = await api.get<ListPage<DiscoveryRun>>('/eval/discovery/runs', {
    params: { limit, offset },
  })
  return r.data
}

export async function createDiscoveryRun(
  body: DiscoveryCreateRequest,
): Promise<DiscoveryRun> {
  const r = await api.post<DiscoveryRun>('/eval/discovery/runs', body)
  return r.data
}

export async function resumeDiscoveryRun(discoveryId: string): Promise<DiscoveryRun> {
  const r = await api.post<DiscoveryRun>(`/eval/discovery/runs/${discoveryId}/resume`)
  return r.data
}

export async function cancelDiscoveryRun(discoveryId: string): Promise<DiscoveryRun> {
  const r = await api.post<DiscoveryRun>(`/eval/discovery/runs/${discoveryId}/cancel`)
  return r.data
}

export interface ThreeArmBar {
  symbol: string
  trade_date: string
  open_minor: number
  high_minor: number
  low_minor: number
  close_minor: number
  volume: number
  snapshot_id: string
}

export interface ThreeArmDecision {
  trade_date: string
  instrument_id: string
  target_weight: number
  decision_at: string
  reason?: string
}

export interface ThreeArmRequest {
  source?: string
  namespace?: string
  family_slug: string
  title: string
  hypothesis: string
  strategy_id: string
  strategy_version: string
  label: Record<string, unknown>
  primary_metric?: string
  initial_cash_minor?: number
  max_test_views?: number
  bars: ThreeArmBar[]
  decisions: ThreeArmDecision[]
}

export interface AxMetrics {
  ax_net_return: number
  ax_cost_after_return: number
  ax_max_drawdown: number
  ax_turnover: number
  ax_n_trades: number
  ax_n_samples: number
  ax_total_fees_minor: number
  ax_traded_notional_minor: number
  ax_llm_cost_usd: number
  ax_provenance: string
  ax_account_id: string
  ax_last_seq: number
  [key: string]: unknown
}

export interface LedgerArmResult {
  arm_name: string
  arm_type: string
  account_id: string
  strategy_id: string
  strategy_version: string
  initial_cash_minor: number
  total_fees_minor: number
  traded_notional_minor: number
  n_trades: number
  llm_cost_usd: number
  net_return: number
  max_drawdown: number
  turnover: number
  provenance: string
  fills: Array<Record<string, unknown>>
  rejections: Array<Record<string, unknown>>
  [key: string]: unknown
}

export interface RecomputedArm {
  net_return: number
  max_drawdown: number
  turnover: number
  n_trades: number
  total_fees_minor: number
  traded_notional_minor: number
  equity_curve: number[]
}

export interface ThreeArmComparison {
  comparison_id: string
  experiment_id: string
  split: string
  bars_digest: string
  arms: Record<string, LedgerArmResult>
  evaluation_ids: string[]
  recomputed: Record<string, RecomputedArm>
  [key: string]: unknown
}

export interface ThreeArmMetrics {
  comparison_id: string
  bars_digest: string
  arm_metrics: Record<string, AxMetrics>
  candidate_excess_vs_buyhold?: number
  candidate_excess_vs_cash?: number
  [key: string]: unknown
}

export interface ThreeArmResult {
  experiment_id: string
  dataset_id: string
  family_id: string
  spec_sha256: string
  holdout_id: string
  consumption_seq: number
  fencing_token: string
  bars_digest: string
  comparison: ThreeArmComparison
  metrics: ThreeArmMetrics
  created_at: string
}

export async function evaluateFixtureThreeArm(
  body: ThreeArmRequest,
): Promise<ThreeArmResult> {
  const r = await api.post<ThreeArmResult>('/eval/three-arm/fixture', body)
  return r.data
}

export async function getEvaluationRun(evaluationId: string): Promise<Record<string, unknown>> {
  const r = await api.get<Record<string, unknown>>(`/eval/runs/${evaluationId}`)
  return r.data
}

export interface PaperProjection {
  account_id: string
  currency: string
  cash_available_minor: number
  cash_frozen_minor: number
  receivable_cash_minor: number
  positions: Record<string, { qty: number; cost_basis_minor: number; sellable_qty: number }>
  state_version: number
  last_seq: number
  last_event_id?: string | null
  generation: number
  updated_at: string
}

export interface PaperAccount {
  account_id: string
  owner_id: string
  namespace: string
  currency: string
  environment: string
  purpose: string
  version?: number
  initial_cash_minor: number
  created_at: string
  [key: string]: unknown
}

export interface PaperAccountCreate {
  account_id: string
  initial_cash_minor: number
  currency?: string
  purpose?: 'paper' | 'evaluation'
}

export async function listPaperAccounts(): Promise<ListPage<PaperAccount>> {
  const r = await api.get<ListPage<PaperAccount>>('/execution/accounts')
  return r.data
}

export async function createPaperAccount(
  body: PaperAccountCreate,
): Promise<{ account: PaperAccount; projection: PaperProjection }> {
  const r = await api.post<{ account: PaperAccount; projection: PaperProjection }>(
    '/execution/accounts',
    body,
  )
  return r.data
}

export async function getPaperAccount(
  accountId: string,
): Promise<PaperAccount & { projection?: PaperProjection }> {
  const r = await api.get<PaperAccount>(`/execution/accounts/${accountId}`)
  return r.data
}

export interface PaperOrder {
  order_id: string
  account_id: string
  instrument_id: string
  side: string
  state: string
  qty_target: number
  qty_filled: number
  qty_cancelled: number
  limit_price_minor?: number | null
  frozen_amount_minor?: number
  frozen_qty?: number
  created_at: string
  updated_at: string
  fills?: Array<Record<string, unknown>>
  [key: string]: unknown
}

export interface PaperIntent {
  intent_id: string
  account_id: string
  idempotency_key: string
  action: string
  instrument_id: string
  state: string
  payload_sha256?: string
  outcome?: {
    status?: string
    reason?: string
    error?: string
    risk?: { passed?: boolean; reason?: string; checks?: string[]; [key: string]: unknown }
    order_id?: string | null
    order_state?: string | null
    fill_event_id?: string | null
    [key: string]: unknown
  }
  created_at: string
  [key: string]: unknown
}

export interface PaperCommit {
  _id: string
  account_id: string
  kind?: string
  seq_start?: number
  seq_end?: number
  idempotency_key?: string
  created_at?: string
  [key: string]: unknown
}

export interface ReconciliationResult {
  account_id: string
  total_commits: number
  cash_discrepancies: Record<string, number>
  position_discrepancies: Record<string, number>
  order_discrepancies: string[]
  business_findings: string[]
  repaired: boolean
  status: 'BALANCED' | 'DISCREPANCY'
  reconciled_at: string
}

export async function listPaperOrders(accountId: string): Promise<{ items: PaperOrder[] }> {
  const r = await api.get<{ items: PaperOrder[] }>(
    `/execution/accounts/${encodeURIComponent(accountId)}/orders`,
  )
  return r.data
}

export async function listPaperIntents(
  accountId: string,
  limit = 50,
): Promise<ListPage<PaperIntent>> {
  const r = await api.get<ListPage<PaperIntent>>(
    `/execution/accounts/${encodeURIComponent(accountId)}/intents`,
    { params: { limit } },
  )
  return r.data
}

export async function listPaperCommits(
  accountId: string,
  limit = 100,
): Promise<{ items: PaperCommit[]; total: number; limit: number }> {
  const r = await api.get<{ items: PaperCommit[]; total: number; limit: number }>(
    `/execution/accounts/${encodeURIComponent(accountId)}/commits`,
    { params: { limit } },
  )
  return r.data
}

export async function reconcilePaperAccount(
  accountId: string,
): Promise<ReconciliationResult> {
  const r = await api.get<ReconciliationResult>(
    `/execution/accounts/${encodeURIComponent(accountId)}/reconcile`,
  )
  return r.data
}

export interface PaperIntentBar {
  symbol: string
  trade_date: string
  open_minor: number
  high_minor: number
  low_minor: number
  close_minor: number
  volume: number
  price_tick?: string
  is_suspended?: boolean
  limit_up_minor?: number
  limit_down_minor?: number
  data_source?: string
  snapshot_id: string
}

export interface PaperIntentRequest {
  action: 'TARGET_POSITION' | 'REDUCE' | 'EXIT'
  instrument_id: string
  idempotency_key: string
  account_state_version: number
  strategy_id: string
  strategy_version: string
  decision_at: string
  earliest_executable_at: string
  expires_at: string
  target: { weight?: number; quantity?: number }
  price_minor: number
  order_constraints?: Record<string, unknown>
  evidence_refs?: string[]
  rationale?: string
  invalidation?: Record<string, unknown>
  market_profile_id?: string
  position_prices_minor?: Record<string, number>
  max_participation_rate?: number
  bar?: PaperIntentBar
  event_at?: string
}

export interface PaperIntentResult {
  intent: Record<string, unknown>
  outcome: Record<string, unknown>
  risk: Record<string, unknown> | null
  order: PaperOrder | null
  fill: { commit_id: string; event: Record<string, unknown> } | null
  replayed: boolean
}

export async function submitPaperIntent(
  accountId: string,
  body: PaperIntentRequest,
): Promise<PaperIntentResult> {
  const r = await api.post<PaperIntentResult>(
    `/execution/accounts/${encodeURIComponent(accountId)}/intents`,
    body,
  )
  return r.data
}

export async function cancelPaperOrder(
  accountId: string,
  orderId: string,
): Promise<{ order: PaperOrder }> {
  const r = await api.post<{ order: PaperOrder }>(
    `/execution/accounts/${encodeURIComponent(accountId)}/orders/${encodeURIComponent(orderId)}/cancel`,
  )
  return r.data
}

export interface AutomationScopeState {
  scope: string
  paused: boolean
  reason: string
  actor?: string
  updated_at?: string
  version: number
  [key: string]: unknown
}

export interface AutomationState {
  global: AutomationScopeState
  scopes: AutomationScopeState[]
}

export async function getAutomationState(): Promise<AutomationState> {
  const r = await api.get<AutomationState>('/automation/state')
  return r.data
}

export async function getAutomationHistory(
  scope = 'global',
  limit = 50,
): Promise<{ items: Array<Record<string, unknown>>; scope: string }> {
  const r = await api.get<{ items: Array<Record<string, unknown>>; scope: string }>(
    '/automation/history',
    { params: { scope, limit } },
  )
  return r.data
}

export async function pauseAutomation(body: {
  scope: string
  reason: string
  expected_version?: number
}): Promise<AutomationScopeState> {
  const r = await api.post<AutomationScopeState>('/automation/pause', body)
  return r.data
}

export async function resumeAutomation(body: {
  scope: string
  reason?: string
  expected_version?: number
}): Promise<AutomationScopeState> {
  const r = await api.post<AutomationScopeState>('/automation/resume', body)
  return r.data
}

export interface DeadLetter {
  dead_letter_id: string
  kind: string
  task: string
  run_id: string
  owner_id: string
  namespace: string
  status: string
  error: string
  attempt?: number
  requeue_count?: number
  created_at: string
  updated_at?: string
  [key: string]: unknown
}

export async function listDeadLetters(params: {
  status?: string
  kind?: string
  all_users?: boolean
  limit?: number
  offset?: number
} = {}): Promise<ListPage<DeadLetter> & { total: number }> {
  const r = await api.get<ListPage<DeadLetter> & { total: number }>(
    '/automation/dead-letters',
    { params },
  )
  return r.data
}

export async function requeueDeadLetter(deadLetterId: string): Promise<DeadLetter> {
  const r = await api.post<DeadLetter>(
    `/automation/dead-letters/${encodeURIComponent(deadLetterId)}/requeue`,
  )
  return r.data
}

export async function abandonDeadLetter(
  deadLetterId: string,
  reason: string,
): Promise<DeadLetter> {
  const r = await api.post<DeadLetter>(
    `/automation/dead-letters/${encodeURIComponent(deadLetterId)}/abandon`,
    { reason },
  )
  return r.data
}
